import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Activity,
  Brain,
  Boxes,
  ChevronRight,
  Copy,
  FileText,
  ExternalLink,
  Loader2,
  MoonStar,
  Play,
  Power,
  RefreshCcw,
  RotateCcw,
  Rocket,
  Server,
  ShieldCheck,
  Square,
  Upload,
  Wallet,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { WebSSHTerminal } from "@/components/jobs/WebSSHTerminal";
import { apiRequest, APIError, buildAPIURL } from "@/lib/api";
import {
  readCloudWalletAccountsPublic,
  type CloudWalletAccountPublic,
  type CloudWalletBalanceSnapshot,
  type CloudWalletSummaryResponse,
} from "@/lib/cloud-wallet";
import { MICRO } from "@/lib/genrl";
import {
  assignHardwareCostBands,
  hardwareCostBandLabel,
} from "@/lib/hardware-cost-band";
import { readPageCache, writePageCache } from "@/lib/page-cache";
import {
  providerLaunchOutcomeMayBeUnknown,
  recoverProviderJobLaunch,
} from "@/lib/provider-launch-recovery";
import {
  providerAccessModeOptions,
  providerCatalogInputOptions,
  providerCostComponentStates,
  providerDescriptorForSlug,
  providerDirectCleanupState,
  providerDirectLaunchIsModelFree,
  providerDirectRawComputeLaunchPayload,
  providerDirectLaunchBlocker,
  providerDirectLaunchProductForAccount,
  providerInputDisplayValue,
  providerInputInitialValues,
  providerInputIsList,
  providerInputValue,
  providerLaunchInputDefaults,
  providerLaunchInputPayload,
  providerJobActionPayload,
  providerMissingRequiredInputs,
  providerManagedWorkloadOptions,
  providerSupportsAction,
  providerSupportsDirectLaunch as registryProviderSupportsDirectLaunch,
  readProviderRegistry,
  type ProviderCatalogOption,
  type ProviderInputValue,
  type ProviderInputValues,
  type ProviderRegistryDescriptor,
  type ProviderRegistryInputField,
} from "@/lib/provider-registry";
import {
  runSnapshotLaunchPayload,
  runSnapshotSummaryLabel,
  runtimeSessionIsActive,
  runtimeSessionStateLabel,
  type RuntimeSessionState,
} from "@/lib/runtime-sessions";
import { cn } from "@/lib/utils";
import {
  WALLET_PROVIDER_HARDWARE_API_PATH,
  walletManagedProviderHardwareRequestPath,
  walletProviderHardwareOptionsFromPayload,
  walletScopedProviderHardwareOptions,
} from "@/lib/wallet-provider-routing";

type ProviderRun = {
  id: string;
  account_id: string;
  account_name: string;
  provider_slug: string;
  provider_name: string;
  remote_id: string;
  remote_name: string;
  status:
    | "launching"
    | "running"
    | "queued"
    | "cleanup_pending"
    | "stopping"
    | "suspending"
    | "suspended"
    | "stopped"
    | "terminated"
    | "failed"
    | "unknown";
  provider_status?: string;
  created_at: string;
  updated_at: string;
  region?: string;
  gpu?: string;
  image?: string;
  public_ip?: string;
  runtime_url?: string;
  last_error?: string;
  launch_request?: Record<string, unknown>;
  web_terminal_supported?: boolean;
};

type RuntimeSessionTurn = {
  id: string;
  session_id: string;
  sequence: number;
  input_text: string;
  output_text?: string;
  status: "queued" | "running" | "completed" | "failed";
  error_message?: string;
  created_at: string;
  updated_at: string;
};

type RuntimeSession = {
  id: string;
  provider_job_id: string;
  account_id: string;
  provider_slug: string;
  provider_name: string;
  session_mode: "persistent";
  name: string;
  model_id?: string;
  runtime_model?: string;
  state: RuntimeSessionState;
  idle_timeout_seconds: number;
  max_ttl_seconds: number;
  auto_stop_idle: boolean;
  wallet_reservation_id?: string;
  runtime_reachable: boolean;
  last_turn_id?: string;
  last_output_preview?: string;
  turn_count: number;
  billing_status: string;
  billing_summary?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  started_at: string;
  last_active_at: string;
  expires_at: string;
  stopped_at?: string;
  last_error?: string;
};

type RuntimeSessionPayload = {
  session: RuntimeSession;
  turn?: RuntimeSessionTurn;
  turns?: RuntimeSessionTurn[];
};

type RuntimeSessionDetailPayload = {
  session?: RuntimeSession;
  turns?: RuntimeSessionTurn[];
};

type ProviderJobProbe = {
  provider_slug: string;
  job_id: string;
  mode: "openai-compatible" | "generic-http" | "raw-compute";
  reachable: boolean;
  runtime_url?: string;
  docs_url?: string;
  public_ip?: string;
  keypair_name?: string;
  models?: string[];
  checked_at: string;
  message: string;
  status_code?: number;
  health_path?: string;
};

type ProviderJobInferenceResult = {
  runtime_url: string;
  mode: "chat" | "completion" | "model_gateway_chat";
  model: string;
  output_text: string;
  checked_at: string;
  model_gateway_provider?: string;
};

type ManagedRuntimeResult = ProviderJobInferenceResult & {
  [key: string]: unknown;
};

type ProviderJobConsoleResult = {
  provider_slug: string;
  job_id: string;
  mode: "vnc-console";
  url: string;
  checked_at: string;
  request_job_id?: string;
  message: string;
};

type ProviderJobActionName =
  | "start"
  | "stop"
  | "restart"
  | "hard-reboot"
  | "hibernate"
  | "hibernate-restore";

type ProviderJobActionResult = {
  action: ProviderJobActionName;
  checked_at: string;
  message: string;
  job: ProviderRun;
};

const PROVIDER_LAUNCH_TIMEOUT_MS = 300_000;
const PROVIDER_LAUNCH_INTENT = "user_confirmed";
const WORKLOADS_PAGE_CACHE_KEY = "run-workloads";
const WORKLOADS_PAGE_CACHE_MAX_AGE_MS = 48 * 60 * 60 * 1000;

type WorkloadsPageCache = {
  accounts: LaunchAccount[];
  jobs: ProviderRun[];
  runtimeSessions?: RuntimeSession[];
  catalogs: Record<string, ProviderLaunchCatalog>;
  hardwareOptions?: AvailableHardwareOption[];
  providerDescriptors?: ProviderRegistryDescriptor[];
};

type AvailableHardwareOption = {
  value: string;
  label: string;
  detail: string;
  gpuCount: number;
  stockAvailable: number;
  providerCount: number;
  providers: string[];
  bestPricePerHour?: number;
  originalPricePerHour?: number;
  discountPercent: number;
  fromProAccount: boolean;
  source: "capacity_table" | "market_offers" | "fallback";
};

type ProviderJobHardwareOptionPayload = {
  hardware_id?: string;
  hardware_label?: string;
  gpu_count?: number;
  stock_available?: number;
  best_price_per_hour?: number;
  best_buyer_price_micros?: number;
  original_buyer_price_micros?: number;
  discount_percent?: number;
  from_pro_account?: boolean;
  provider_count?: number;
  providers?: string[];
  regions?: string[];
  runtime_classes?: string[];
  pro_access_modes?: string[];
};

type ProviderJobHardwarePayload = {
  hardware_options?: ProviderJobHardwareOptionPayload[];
  refresh_error?: string;
};

type MarketOffer = {
  option_id?: string;
  flavor_name?: string;
  region_name?: string;
  gpu?: string;
  gpu_type?: string;
  gpu_count?: number;
  stock_available?: number;
  price_per_hour?: number;
  buyer_unit_price_micros?: number;
  provider_name?: string;
  provider_slug?: string;
  source?: string;
};

type MarketOffersPayload = {
  offers?: MarketOffer[];
};

function userSafeLaunchError(error: unknown) {
  const fallback = "GENRL managed compute is temporarily unavailable. Please try again shortly.";
  const detail =
    typeof error === "string"
      ? error
      : error instanceof APIError
        ? String(error.detail || "")
        : error instanceof Error
          ? error.message
          : "";
  const normalized = detail.toLowerCase();
  if (
    normalized.includes("err_insufficient_credits") ||
    normalized.includes("insufficient credits")
  ) {
    return "Your GENRL wallet needs available credits before this workload can launch. Add GENRL credits in Wallet, then run it again.";
  }
  if (
    normalized.includes("account balance is too low") ||
    normalized.includes("too low to rent a pod") ||
    normalized.includes("add funds to your account")
  ) {
    return "RunPod rejected the launch because the GENRL-managed RunPod account balance is too low. No VM or selected model runtime started, so no output was produced.";
  }
  if (
    normalized.includes("price changed") ||
    normalized.includes("price is unavailable") ||
    normalized.includes("quoted price") ||
    normalized.includes("hourly rate")
  ) {
    return "The selected hardware cost band could not be confirmed before launch. Refresh the available hardware and choose again.";
  }
  if (
    normalized.includes("no instances currently available") ||
    normalized.includes("insufficient capacity") ||
    normalized.includes("no launchable") ||
    normalized.includes("no eligible") ||
    normalized.includes("capacity")
  ) {
    return "No GENRL managed provider capacity matched those workload filters just now. Try Auto hardware or launch again to re-route across fresh capacity.";
  }
  if (
    normalized.includes("provider credentials") ||
    (normalized.includes("no genrl provider") &&
      !normalized.includes("could launch this workload")) ||
    normalized.includes("not configured") ||
    normalized.includes("unauthorized") ||
    normalized.includes("forbidden") ||
    normalized.includes("status=401") ||
    normalized.includes("status=403") ||
    normalized.includes("api key")
  ) {
    return fallback;
  }
  if (error instanceof APIError && error.code === "ERR_TIMEOUT") {
    return "GENRL is still routing this workload. Job history has been refreshed; if no job appears, launch again to re-route across fresh capacity.";
  }
  return detail || "Could not launch compute.";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function snapshotContextLabelFromRecord(record: Record<string, unknown> | null | undefined) {
  const snapshotPolicy = asRecord(record?.snapshot_policy);
  const mode = String(snapshotPolicy?.mode || "")
    .trim()
    .toLowerCase();
  if (record?.save_run_context === false || mode === "none") return runSnapshotSummaryLabel(false);
  if (record?.save_run_context === true || mode === "outputs_and_logs")
    return runSnapshotSummaryLabel(true);
  return "—";
}

function workloadContextLabel(job: ProviderRun | null | undefined) {
  return snapshotContextLabelFromRecord(asRecord(job?.launch_request));
}

function runtimeSessionContextLabel(session: RuntimeSession | null | undefined) {
  return snapshotContextLabelFromRecord(asRecord(session?.metadata));
}

function managedRuntimeStatus(job: ProviderRun | null | undefined) {
  return String(job?.launch_request?.managed_runtime_status || "").trim();
}

function firstRuntimeText(...values: unknown[]) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function runtimeOutputFromResult(result: Record<string, unknown>) {
  const direct = firstRuntimeText(
    result.output_text,
    result.text,
    result.content,
    result.message,
    result.response,
  );
  if (direct) return direct;

  const choices = Array.isArray(result.choices) ? result.choices : [];
  for (const choice of choices) {
    const row = asRecord(choice);
    if (!row) continue;
    const message = asRecord(row.message);
    const nested = firstRuntimeText(row.text, row.content, message?.content, message?.text);
    if (nested) return nested;
  }
  return "";
}

function managedRuntimeResult(job: ProviderRun | null | undefined): ManagedRuntimeResult | null {
  const result = asRecord(job?.launch_request?.managed_runtime_result);
  if (!result) return null;
  const output = runtimeOutputFromResult(result);
  const runtimeURL = String(result.runtime_url || job?.runtime_url || "").trim();
  const checkedAt = String(result.checked_at || job?.updated_at || "").trim();
  const model = String(
    result.model || job?.launch_request?.runtime_model || job?.launch_request?.model_id || "",
  ).trim();
  if (!output) return null;
  return {
    ...result,
    output_text: output,
    runtime_url: runtimeURL,
    checked_at: checkedAt,
    model,
    mode:
      result.mode === "completion"
        ? "completion"
        : result.mode === "model_gateway_chat"
          ? "model_gateway_chat"
          : "chat",
  };
}

function runSnapshotID(result: ManagedRuntimeResult | null | undefined) {
  return String(result?.run_snapshot_id || "").trim();
}

function runSnapshotFileURL(
  result: ManagedRuntimeResult | null | undefined,
  file: "manifest" | "output",
) {
  const id = runSnapshotID(result);
  return id ? buildAPIURL(`/api/v1/run-snapshots/${encodeURIComponent(id)}/files/${file}`) : "";
}

function runtimeResultModeLabel(result: ProviderJobInferenceResult | null | undefined) {
  if (!result) return "Runtime";
  if (result.mode === "model_gateway_chat") {
    const provider = String(result.model_gateway_provider || "").trim();
    return provider ? `${provider} model gateway` : "Model gateway";
  }
  return result.mode === "chat" ? "Chat completion" : "Text completion";
}

function managedRuntimeError(job: ProviderRun | null | undefined) {
  return String(job?.launch_request?.managed_runtime_error || "").trim();
}

function managedRuntimeFailed(job: ProviderRun | null | undefined) {
  const state = managedRuntimeStatus(job).toLowerCase();
  return state === "not_ready" || state === "prompt_failed" || !!managedRuntimeError(job);
}

function workloadHasClosed(job: ProviderRun) {
  return (
    ["stopped", "suspended", "failed"].includes(job.status) ||
    providerDirectCleanupState(job) !== "not_requested" ||
    managedRuntimeFailed(job)
  );
}

function workloadIsActive(job: ProviderRun) {
  return (
    ["launching", "queued", "running", "stopping", "suspending"].includes(job.status) &&
    !managedRuntimeResult(job) &&
    !managedRuntimeFailed(job)
  );
}

function managedRuntimePrompt(job: ProviderRun | null | undefined) {
  return String(job?.launch_request?.prompt || "").trim();
}

function finiteNumber(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function firstFiniteNumber(...values: unknown[]) {
  for (const value of values) {
    const number = finiteNumber(value);
    if (number != null) return number;
  }
  return null;
}

function parseTimeMs(value?: string) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatElapsed(ms: number) {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return remainingSeconds ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function workloadDurationLabel(job: ProviderRun, result: ManagedRuntimeResult | null) {
  const start = parseTimeMs(job.created_at);
  const finish = parseTimeMs(
    result?.checked_at || (workloadHasClosed(job) ? job.updated_at : undefined),
  );
  if (start != null && finish != null && finish >= start) {
    return formatElapsed(finish - start);
  }
  if (start != null && workloadIsActive(job)) {
    return `${formatElapsed(Date.now() - start)} so far`;
  }
  return "Pending";
}

function formatCredits(value: number) {
  return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} credits`;
}

function workloadSettlementStatus(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  const reconciliationStatus = firstRuntimeText(
    reconciliation.settlement_status,
    reconciliation.status,
  );
  const launchStatus = firstRuntimeText(
    launchRequest.wallet_settlement_status,
    launchRequest.settlement_status,
    launchRequest.wallet_price_state,
  ).toLowerCase();
  const status = firstRuntimeText(reconciliationStatus, launchStatus).toLowerCase();
  if (status) return status;
  if (firstRuntimeText(launchRequest.wallet_reservation_id, reconciliation.reservation_id)) {
    return "reserved";
  }
  return "";
}

function workloadPriceFinalized(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const explicit = launchRequest.wallet_price_finalized;
  if (typeof explicit === "boolean") return explicit;
  return workloadSettlementStatus(job) === "settled";
}

function workloadPriceFactLabel(job: ProviderRun) {
  const settlementStatus = workloadSettlementStatus(job);
  if (workloadPriceFinalized(job)) return "Final price";
  if (settlementStatus === "settlement_failed") return "Price";
  if (settlementStatus === "released") return "Reservation";
  return "Reserved max";
}

function workloadCostLabel(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  const settlementStatus = workloadSettlementStatus(job);
  const capturedMicros = firstFiniteNumber(
    launchRequest.wallet_final_price_micros,
    launchRequest.wallet_final_buyer_debit_micros,
    launchRequest.final_buyer_debit_micros,
    launchRequest.wallet_captured_amount_micros,
    launchRequest.wallet_settled_amount_micros,
    launchRequest.wallet_debit_amount_micros,
    launchRequest.wallet_actual_amount_micros,
    launchRequest.charge_amount_micros,
    launchRequest.billed_amount_micros,
    launchRequest.captured_amount_micros,
    reconciliation.captured_amount_micros,
    reconciliation.final_buyer_debit_micros,
  );
  if (capturedMicros != null && (capturedMicros > 0 || settlementStatus === "settled")) {
    return `Final ${formatCredits(capturedMicros / MICRO)}`;
  }
  if (settlementStatus === "settlement_failed") return "Settlement failed";
  if (settlementStatus === "released") return "Reserve released";

  const reserveMicros = firstFiniteNumber(
    launchRequest.wallet_reserve_amount_micros,
    launchRequest.reserve_amount_micros,
    reconciliation.reserved_amount_micros,
  );
  if (reserveMicros != null && reserveMicros > 0)
    return `Up to ${formatCredits(reserveMicros / MICRO)} reserved`;

  const billedUsd = firstFiniteNumber(
    (job as Record<string, unknown>).billed_total_value,
    launchRequest.billed_total_value,
    launchRequest.charge_value,
    launchRequest.cost_usd,
  );
  if (billedUsd != null && billedUsd > 0) return fmtUsd(billedUsd);

  return workloadHasClosed(job) ? "No settled cost yet" : "Pending settlement";
}

function workloadCostCredits(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  const capturedMicros = firstFiniteNumber(
    launchRequest.wallet_final_buyer_debit_micros,
    launchRequest.final_buyer_debit_micros,
    launchRequest.wallet_captured_amount_micros,
    launchRequest.wallet_settled_amount_micros,
    launchRequest.wallet_debit_amount_micros,
    launchRequest.wallet_actual_amount_micros,
    launchRequest.charge_amount_micros,
    launchRequest.billed_amount_micros,
    launchRequest.captured_amount_micros,
    reconciliation.captured_amount_micros,
    reconciliation.final_buyer_debit_micros,
  );
  return capturedMicros != null && capturedMicros > 0 ? capturedMicros / MICRO : 0;
}

function workloadBillableSeconds(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  return firstFiniteNumber(
    launchRequest.wallet_billable_seconds,
    launchRequest.billable_seconds,
    reconciliation.billable_seconds,
  );
}

function workloadHourlyRateMicros(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  const explicitMicros = firstFiniteNumber(
    launchRequest.wallet_buyer_unit_price_micros,
    launchRequest.wallet_buyer_unit_price_micros_hr,
    launchRequest.buyer_unit_price_micros_hr,
    launchRequest.buyer_price_micros,
    reconciliation.buyer_unit_price_micros_hr,
    reconciliation.buyer_price_micros,
  );
  if (explicitMicros != null && explicitMicros > 0) return explicitMicros;

  const pricePerHour = firstFiniteNumber(
    launchRequest.price_per_hour,
    reconciliation.price_per_hour,
  );
  if (pricePerHour != null && pricePerHour > 0) return pricePerHour * MICRO;

  const capturedCredits = workloadCostCredits(job);
  const seconds = workloadBillableSeconds(job);
  const gpuCount = firstFiniteNumber(launchRequest.gpu_count, reconciliation.gpu_count) || 1;
  if (capturedCredits > 0 && seconds != null && seconds > 0 && gpuCount > 0) {
    return (capturedCredits * MICRO * 3600) / (seconds * gpuCount);
  }
  return 0;
}

function workloadBillableTimeLabel(job: ProviderRun) {
  const seconds = workloadBillableSeconds(job);
  return seconds != null && seconds > 0 ? formatElapsed(seconds * 1000) : "Pending";
}

function isGenericHardwareLabel(value: string) {
  const text = value.trim().toLowerCase();
  return !text || /^gpu\s*x?\s*\d*$/.test(text) || text === "unknown";
}

function hardwareLabelSpecificity(value: string) {
  const text = value.trim();
  if (isGenericHardwareLabel(text)) return 0;

  let score = text.length;
  if (/\b(nvidia|geforce|rtx|gtx|tesla|amd|radeon|mi\d+|intel|arc)\b/i.test(text)) {
    score += 100;
  }
  if (/\b(a|h|l|v|t|p)?\d{3,5}\b/i.test(text)) {
    score += 20;
  }
  if (/\b\d+\s*gb\b/i.test(text)) {
    score += 10;
  }
  return score;
}

function mostSpecificHardwareLabel(...values: unknown[]) {
  let best = "";
  let bestScore = 0;
  for (const value of values) {
    const text = String(value || "").trim();
    const score = hardwareLabelSpecificity(text);
    if (score > bestScore) {
      best = text;
      bestScore = score;
    }
  }
  return best;
}

function workloadHardwareLabel(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  const resource = asRecord(launchRequest.provider_resource) || {};
  const accelerator = asRecord(resource.accelerator) || {};
  const observedHardware = firstRuntimeText(accelerator.model, accelerator.type_id);
  const hardware =
    observedHardware ||
    mostSpecificHardwareLabel(
      launchRequest.gpu_type_id,
      launchRequest.gpu_type_label,
      launchRequest.gpu_type,
      launchRequest.gpu_label,
      launchRequest.hardware_label,
      launchRequest.hardware_type,
      reconciliation.gpu_type_id,
      reconciliation.gpu_type_label,
      reconciliation.gpu_type,
      reconciliation.gpu_label,
      reconciliation.hardware_label,
      launchRequest.flavor_name,
      launchRequest.instance_type,
      job.gpu,
    );
  const gpuCount = firstFiniteNumber(
    accelerator.device_count,
    launchRequest.gpu_count,
    reconciliation.gpu_count,
  );
  const shape = firstRuntimeText(
    resource.shape_id,
    launchRequest.shape_id,
    launchRequest.machine_type,
    launchRequest.flavor_name,
    launchRequest.instance_type,
  );
  if (!hardware) return shape || "Hardware pending";
  const fractionNumerator = firstFiniteNumber(accelerator.fraction_numerator);
  const fractionDenominator = firstFiniteNumber(accelerator.fraction_denominator);
  const fraction =
    fractionNumerator != null &&
    fractionNumerator > 0 &&
    fractionDenominator != null &&
    fractionDenominator > 1
      ? `${fractionNumerator}/${fractionDenominator} GPU`
      : "";
  const countAlreadyVisible =
    gpuCount != null && new RegExp(`(?:^|\\D)${gpuCount}(?:\\D|$)`).test(hardware);
  let label = hardware;
  if (fraction) {
    label = `${hardware} · ${fraction}`;
  } else if (gpuCount != null && gpuCount > 1 && !countAlreadyVisible) {
    label = `${hardware} x${gpuCount}`;
  }
  const vram = firstFiniteNumber(accelerator.vram_gb);
  if (vram != null && vram > 0 && !new RegExp(`\\b${vram}\\s*GB\\b`, "i").test(label)) {
    const memory =
      !fraction && gpuCount != null && gpuCount > 1
        ? `${vram} GB each · ${(vram * gpuCount).toLocaleString()} GB total VRAM`
        : `${vram} GB VRAM`;
    label = `${label} · ${memory}`;
  }
  const architecture = firstRuntimeText(resource.architecture);
  if (architecture && architecture.toLowerCase() !== "x86_64") {
    label = `${label} · ${architecture}`;
  }
  if (shape && !label.toLowerCase().includes(shape.toLowerCase())) {
    return `${label} · ${shape}`;
  }
  return label;
}

function workloadProviderLocationLabel(job: ProviderRun) {
  const provider = firstRuntimeText(job.provider_name, job.provider_slug, "Provider pending");
  const region = firstRuntimeText(job.region);
  return region ? `${provider} · ${region}` : provider;
}

function workloadBillingDetail(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  const reconciliation = asRecord(launchRequest.wallet_reconciliation) || {};
  const settlementStatus = workloadSettlementStatus(job);
  if (settlementStatus === "settled") {
    return "Final price captured; unused reserve was released.";
  }
  if (settlementStatus === "settlement_failed") {
    return firstRuntimeText(
      launchRequest.managed_runtime_settlement_error,
      reconciliation.last_error,
      "Final price settlement failed; the reservation needs reconciliation.",
    );
  }
  if (settlementStatus === "released") {
    return "The reserved credits were released because the workload did not produce a billable final answer.";
  }
  if (job.last_error && !managedRuntimeResult(job)) {
    return "The workload failed before runtime output, so no final price was captured. The wallet reservation is being released or reconciled.";
  }
  return "Final price is still awaiting settlement; the shown amount is only the reserve cap.";
}

function workloadServiceName(job: ProviderRun) {
  const launchRequest = asRecord(job.launch_request) || {};
  return firstRuntimeText(
    launchRequest.service_name,
    launchRequest.name,
    job.remote_name,
    "Provider job",
  );
}

function workloadInputLabel(job: ProviderRun) {
  const prompt = managedRuntimePrompt(job);
  if (prompt) return prompt;
  const launchRequest = asRecord(job.launch_request) || {};
  const model = firstRuntimeText(launchRequest.runtime_model, launchRequest.model_id);
  const workload = firstRuntimeText(launchRequest.workload_type, job.remote_name);
  if (model && workload) return `${workload} using ${model}`;
  return workload || "Compute workload";
}

function workloadProof(job: ProviderRun, result: ManagedRuntimeResult | null) {
  const launchRequest = asRecord(job.launch_request) || {};
  const verificationStatus = firstRuntimeText(launchRequest.verification_status).toLowerCase();
  const verificationSummary = firstRuntimeText(launchRequest.verification_summary);
  const priceFinalized = workloadPriceFinalized(job);
  const hardware = workloadHardwareLabel(job);
  const hardwareRecorded = hardware !== "Hardware pending";
  if (job.last_error && !result) {
    return {
      label: "Launch failed",
      detail: userSafeLaunchError(job.last_error),
      tone: "pending" as const,
    };
  }
  if (verificationStatus === "verified_final") {
    return {
      label: "Verified final",
      detail:
        verificationSummary ||
        "Runtime output, provider hardware identity, and final wallet settlement are recorded.",
      tone: "verified" as const,
    };
  }
  if (
    verificationStatus === "runtime_verified_pending_settlement" ||
    (result && hardwareRecorded && !priceFinalized)
  ) {
    return {
      label: "Price pending",
      detail:
        verificationSummary ||
        "Runtime output and provider hardware identity are recorded; final wallet settlement is still pending.",
      tone: "pending" as const,
    };
  }
  if (verificationStatus === "released_without_output") {
    return {
      label: "Reserve released",
      detail:
        verificationSummary ||
        "The wallet reservation was released because this workload did not complete a billable prompt.",
      tone: "pending" as const,
    };
  }
  if (verificationStatus === "settlement_failed") {
    return {
      label: "Settlement failed",
      detail:
        verificationSummary ||
        "The workload needs wallet settlement reconciliation before the final price can be shown.",
      tone: "pending" as const,
    };
  }
  const attestation =
    asRecord(launchRequest.attestation) ||
    asRecord(launchRequest.attestation_evidence) ||
    asRecord(launchRequest.vm_attestation) ||
    null;
  const attestationStatus = firstRuntimeText(
    launchRequest.attestation_status,
    launchRequest.vm_attestation_status,
    attestation?.status,
    attestation?.verified,
  ).toLowerCase();
  if (["verified", "valid", "ok", "true", "passed"].includes(attestationStatus) && priceFinalized) {
    return {
      label: "Attested VM",
      detail: "Provider attestation evidence was verified for this VM.",
      tone: "verified" as const,
    };
  }

  const resultRuntime = String(result?.runtime_url || "").trim();
  const jobRuntime = String(job.runtime_url || "").trim();
  if (
    result &&
    jobRuntime &&
    resultRuntime &&
    resultRuntime.startsWith(jobRuntime) &&
    priceFinalized
  ) {
    return {
      label: "Runtime matched VM",
      detail:
        "The answer came back from the provisioned runtime URL for this provider VM, and final wallet settlement is recorded.",
      tone: "verified" as const,
    };
  }
  if (result && (job.remote_id || resultRuntime || job.public_ip) && priceFinalized) {
    return {
      label: "Runtime answered from VM",
      detail:
        "The managed runtime returned output from the provider resource recorded for this workload, and final wallet settlement is recorded.",
      tone: "verified" as const,
    };
  }
  if (result && (job.remote_id || resultRuntime || job.public_ip)) {
    return {
      label: "Runtime recorded",
      detail:
        "The managed runtime returned output from the provider resource; final wallet settlement is still pending.",
      tone: "pending" as const,
    };
  }
  if (job.remote_id || job.public_ip || job.runtime_url) {
    return {
      label: "VM evidence recorded",
      detail: "GENRL has provider VM identity evidence; runtime proof is still pending.",
      tone: "pending" as const,
    };
  }
  return {
    label: "Proof pending",
    detail: "GENRL has not received VM proof for this workload yet.",
    tone: "pending" as const,
  };
}

function workloadSortTime(job: ProviderRun) {
  const result = managedRuntimeResult(job);
  return parseTimeMs(result?.checked_at || job.updated_at || job.created_at) || 0;
}

function workloadOutputLabel(
  job: ProviderRun,
  result: ManagedRuntimeResult | null,
  runtimeState: string,
  runtimeError: string,
) {
  const terminalWithoutResult = !result && workloadHasClosed(job);
  if (result) return runtimeResultIssue(job, result) || result.output_text;
  if (runtimeError) return userSafeLaunchError(runtimeError);
  if (job.last_error) return userSafeLaunchError(job.last_error);
  if (runtimeState === "starting")
    return "Runtime is starting. GENRL will run the prompt automatically.";
  if (runtimeState === "ready") return "Runtime is ready. Prompt execution is in progress.";
  if (terminalWithoutResult) return "No output was returned before this run ended.";
  return "GENRL is provisioning and will attach the output here.";
}

function runtimeResultIsVerificationOnly(result: ManagedRuntimeResult | null | undefined) {
  const output = String(result?.output_text || "")
    .trim()
    .toLowerCase();
  return (
    output.startsWith("verified managed workload completed:") ||
    (output.startsWith("genrl managed workload completed.") &&
      output.includes("the workload vm accepted the prompt"))
  );
}

function promptRequestsChinese(prompt: string) {
  const lower = prompt.toLowerCase();
  return (
    lower.includes("chinese") ||
    lower.includes("mandarin") ||
    prompt.includes("中文") ||
    prompt.includes("汉语") ||
    prompt.includes("漢語") ||
    prompt.includes("普通话") ||
    prompt.includes("普通話")
  );
}

function containsHan(text: string) {
  return /[\u3400-\u9fff\uf900-\ufaff]/u.test(text);
}

function outputLooksLikeReasoning(text: string) {
  const lower = text.trim().toLowerCase();
  if (!lower) return false;
  const prefixes = [
    "okay, the user",
    "ok, the user",
    "the user asked",
    "we need answer",
    "we need to answer",
    "i need to",
    "let me think",
    "maybe ",
    "i should ",
  ];
  if (prefixes.some((prefix) => lower.startsWith(prefix))) return true;
  const markers = [
    "the user asked",
    "asked me to",
    "i need to",
    "let me think",
    "i should",
    "i'll ",
    "maybe ",
    "make sure",
    "the prompt",
  ];
  return markers.filter((marker) => lower.includes(marker)).length >= 3;
}

function runtimeResultIssue(job: ProviderRun, result: ManagedRuntimeResult | null | undefined) {
  if (!result) return "";
  const output = String(result.output_text || "").trim();
  const finishReason = String(result.finish_reason || "")
    .trim()
    .toLowerCase();
  if (finishReason === "length" || finishReason === "max_tokens") {
    return "The runtime stopped at the token limit before producing a final answer. Please rerun this prompt.";
  }
  if (promptRequestsChinese(workloadInputLabel(job)) && !containsHan(output)) {
    return "The runtime returned non-Chinese text for a Chinese-language prompt. Please rerun this prompt.";
  }
  if (outputLooksLikeReasoning(output)) {
    return "The runtime returned internal reasoning instead of a final answer. Please rerun this prompt.";
  }
  return "";
}

function isManagedWalletJob(job: ProviderRun | null | undefined) {
  const routeType = String(job?.launch_request?.wallet_route_type || "")
    .trim()
    .toLowerCase();
  if (routeType === "genrl_abstracted") return true;
  const accountID = String(job?.account_id || "")
    .trim()
    .toLowerCase();
  return (
    accountID.startsWith("genrl-managed-") &&
    (!!managedRuntimeStatus(job) || !!String(job?.launch_request?.prompt || "").trim())
  );
}

function isPersistentRuntimeContainerJob(job: ProviderRun | null | undefined) {
  const launchRequest = job?.launch_request || {};
  return (
    Boolean(launchRequest.persist_vm) ||
    String(launchRequest.session_mode || "")
      .trim()
      .toLowerCase() === "persistent" ||
    String(launchRequest.runtime_session_id || "").trim() !== ""
  );
}

type LaunchFormState = {
  account_id: string;
  provider_slug: string;
  name: string;
  workload_type: string;
  pack_mode: "auto" | "manual";
  selected_packs: string[];
  model_id: string;
  use_custom_model: boolean;
  custom_model_ref: string;
  input_data_uri: string;
  prompt: string;
  session_mode: "one_shot" | "persistent";
  idle_timeout_seconds: number;
  max_ttl_seconds: number;
  auto_stop_idle: boolean;
  save_run_context: boolean;
  routing_preference: string;
  max_price_credits: number;
  estimated_minutes: number;
  environment_name: string;
  flavor_name: string;
  image_name: string;
  assign_public_ip: boolean;
  key_name: string;
  gpu_type_id: string;
  gpu_count: number;
  cloud_type: string;
  docker_image: string;
  start_command: string;
  container_disk_gb: number;
  volume_gb: number;
  port_spec: string;
  restore_context: Record<string, unknown> | null;
};

type WorkloadPlanPreview = {
  effective_workload_type: string;
  task_intent: string;
  required_packs: string[];
  allowed_packs?: string[];
  requires_execution: boolean;
  runtime_agent_required: boolean;
  model_gateway_allowed: boolean;
  warnings?: string[];
  reasons?: string[];
};

type WorkloadPlanResponse = {
  plan: WorkloadPlanPreview;
  launch_request?: Record<string, unknown>;
};

type RunSnapshotRerunDraftResponse = {
  launch_request?: Record<string, unknown>;
};

type WorkloadArtifactUpload = {
  artifact_ref?: string;
  source_uri?: string;
  filename: string;
  size_bytes?: number;
  content_type?: string;
  kind: "data" | "weights";
  source: "browser_upload" | "external_uri";
};

type WorkloadUploadStatus = {
  job_id?: string;
  artifact_ref?: string;
  received_bytes?: number;
  total_bytes?: number;
  completed?: boolean;
};

type LaunchAccount = CloudWalletAccountPublic & {
  snapshot?: CloudWalletBalanceSnapshot;
  directLaunch: boolean;
  launchReason: string;
};

type HyperstackCatalogEnvironment = {
  name: string;
  region?: string;
};

type HyperstackCatalogFlavor = {
  name: string;
  region?: string;
  gpu?: string;
  gpu_count?: number;
  cpu?: number;
  ram_gb?: number;
  price_per_hour?: number;
  stock_available?: number;
};

type HyperstackCatalogImage = {
  name: string;
  region?: string;
  type?: string;
  version?: string;
  is_public?: boolean;
};

type HyperstackCatalogKeypair = {
  name: string;
  environment_name?: string;
  region?: string;
};

type HyperstackProviderLaunchCatalog = {
  provider_slug: "hyperstack";
  environments: HyperstackCatalogEnvironment[];
  flavors: HyperstackCatalogFlavor[];
  images: HyperstackCatalogImage[];
  keypairs: HyperstackCatalogKeypair[];
};

type RunPodCatalogPrice = {
  stock_status?: string;
  uninterruptable_price?: number;
  available_gpu_counts?: number[];
  max_unreserved_gpu_count?: number;
  country_code?: string;
};

type RunPodCatalogGPUType = {
  id: string;
  display_name: string;
  memory_gb?: number;
  secure_cloud: boolean;
  community_cloud: boolean;
  secure?: RunPodCatalogPrice;
  community?: RunPodCatalogPrice;
};

type RunPodProviderLaunchCatalog = {
  provider_slug: "runpod";
  gpu_types: RunPodCatalogGPUType[];
  cloud_types: string[];
  defaults: {
    docker_image: string;
    start_command: string;
    port_spec: string;
    container_disk_gb: number;
    volume_gb: number;
  };
};

type RegistryProviderLaunchCatalog = Record<string, unknown> & { provider_slug: string };

type ProviderLaunchCatalog =
  | HyperstackProviderLaunchCatalog
  | RunPodProviderLaunchCatalog
  | RegistryProviderLaunchCatalog;

const REFRESH_MS = 30_000;

const ABSTRACTED_WORKLOAD_OPTIONS = [
  { value: "text_generation", label: "Text generation" },
  { value: "embeddings", label: "Embeddings" },
  { value: "batch_inference", label: "Batch inference" },
  { value: "fine_tuning", label: "Fine-tuning" },
] as const;

const ABSTRACTED_MODEL_OPTIONS = [
  { value: "genrl-recommended", label: "GENRL recommended" },
  { value: "deepseek-ai/DeepSeek-R1-Distill-Qwen-7B", label: "DeepSeek R1 Distill Qwen 7B" },
  { value: "mistralai/Mistral-7B-Instruct-v0.3", label: "Mistral 7B Instruct" },
  { value: "Qwen/Qwen2.5-7B-Instruct", label: "Qwen 2.5 7B Instruct" },
] as const;

const ABSTRACTED_HARDWARE_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "H100", label: "H100" },
  { value: "A100", label: "A100" },
  { value: "L40S", label: "L40S" },
  { value: "RTX A6000", label: "RTX A6000" },
] as const;

const LEGACY_ABSTRACTED_PROVIDER_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "runpod", label: "RunPod" },
  { value: "hyperstack", label: "Hyperstack" },
  { value: "lambda", label: "Lambda" },
] as const;

const ABSTRACTED_ROUTING_OPTIONS = [
  { value: "cheapest", label: "Cheapest", hint: "Lowest verified price" },
  { value: "fastest", label: "Fastest", hint: "Earliest capacity" },
  { value: "quality", label: "Highest quality", hint: "Best reliability score" },
] as const;

const WORKLOAD_PACK_OPTIONS = [
  { value: "code", label: "Code" },
  { value: "data", label: "Data" },
  { value: "document", label: "Docs" },
  { value: "web", label: "Web" },
  { value: "finance", label: "Finance" },
  { value: "api", label: "API" },
] as const;

function optionLabel(options: ReadonlyArray<{ value: string; label: string }>, value: string) {
  return options.find((option) => option.value === value)?.label || value || "Auto";
}

function estimatedMinutes(workloadType: string, routingPreference: string) {
  const base = workloadType === "fine_tuning" ? 45 : workloadType === "batch_inference" ? 18 : 8;
  if (routingPreference === "fastest") return Math.max(3, base - 4);
  if (routingPreference === "quality") return base + 6;
  return base;
}

function reserveMicrosFromCredits(value: number) {
  return Math.max(1, Math.round(Math.max(0, Number(value || 0)) * MICRO));
}

function fileExtension(name: string) {
  const index = String(name || "")
    .toLowerCase()
    .lastIndexOf(".");
  return index >= 0
    ? String(name || "")
        .toLowerCase()
        .slice(index)
    : "";
}

function inferWorkloadPlanPreview(form: LaunchFormState, files: File[]): WorkloadPlanPreview {
  const prompt = String(form.prompt || "").toLowerCase();
  const extensions = files.map((file) => fileExtension(file.name));
  const hasCode = extensions.some((ext) =>
    [".py", ".ipynb", ".r", ".js", ".ts", ".sh", ".sql", ".go"].includes(ext),
  );
  const hasData = extensions.some((ext) =>
    [".csv", ".tsv", ".xlsx", ".xls", ".json", ".jsonl", ".parquet", ".sqlite", ".duckdb"].includes(
      ext,
    ),
  );
  const hasDocument = extensions.some((ext) =>
    [".pdf", ".docx", ".pptx", ".md", ".html"].includes(ext),
  );
  const asksExecution = [
    "run",
    "execute",
    "output",
    "predict",
    "forecast",
    "debug",
    "calculate",
    "compute",
    "sp500",
    "s&p",
  ].some((word) => prompt.includes(word));
  let effective = form.workload_type || "text_generation";
  let intent = "answer_prompt";
  if (effective === "text_generation") {
    if (hasCode && asksExecution) {
      effective = "code_execution";
      intent = "execute_code";
    } else if (hasData && asksExecution) {
      effective = "data_analysis";
      intent = "analyze_data";
    } else if (hasDocument && /document|pdf|extract|summari[sz]e|read this file/.test(prompt)) {
      effective = "document_analysis";
      intent = "analyze_document";
    }
  }
  const requiresExecution = [
    "code_execution",
    "data_analysis",
    "document_analysis",
    "database_query",
    "web_research",
    "finance_analysis",
    "api_task",
  ].includes(effective);
  const required = new Set(["core", "verification"]);
  if (files.length > 0 || String(form.input_data_uri || "").trim()) required.add("artifact");
  if (effective === "code_execution") {
    required.add("code");
    required.add("data");
  }
  if (effective === "data_analysis") required.add("data");
  if (effective === "document_analysis") required.add("document");
  if (effective === "web_research") required.add("web");
  if (effective === "finance_analysis") required.add("finance");
  if (effective === "api_task") required.add("api");
  if (!requiresExecution) required.add("model_runtime");
  const selected = new Set(form.pack_mode === "manual" ? form.selected_packs : []);
  const allowed = new Set([...required, ...selected]);
  return {
    effective_workload_type: effective,
    task_intent: intent,
    required_packs: [...required].sort(),
    allowed_packs: [...allowed].sort(),
    requires_execution: requiresExecution,
    runtime_agent_required: requiresExecution,
    model_gateway_allowed: !requiresExecution,
  };
}

function createClientLaunchID() {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `ui_launch_${random}`;
}

function formatBytes(value?: number) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`;
}

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function appendUniqueFiles(current: File[], incoming: FileList | File[]) {
  const next = [...current];
  const seen = new Set(next.map(fileKey));
  Array.from(incoming).forEach((file) => {
    const key = fileKey(file);
    if (seen.has(key)) return;
    seen.add(key);
    next.push(file);
  });
  return next;
}

function workloadUploadTimeoutMs(file: File) {
  return Math.max(120_000, Math.ceil(file.size / 50));
}

function workloadUploadChunkTimeoutMs(chunk: Blob) {
  return Math.max(60_000, Math.ceil(chunk.size / 50));
}

function normalizeUploadChunkSize(value?: number) {
  const fallback = 16 * 1024 * 1024;
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return fallback;
  return Math.min(Math.max(Math.floor(size), 1024 * 1024), 64 * 1024 * 1024);
}

async function sha256Hex(blob: Blob) {
  const buffer = await blob.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function supportsDirectLaunch(
  providerSlug: string,
  accountID: string,
  providers: ProviderRegistryDescriptor[] = [],
) {
  const slug = String(providerSlug || "")
    .trim()
    .toLowerCase();
  const descriptor = providerDescriptorForSlug(providers, slug);
  if (descriptor) {
    const hasLaunchUIContract = descriptor.launch_inputs.length > 0;
    const hasLegacyLaunchUI = slug === "hyperstack" || slug === "runpod";
    const product = providerDirectLaunchProductForAccount(accountID);
    return registryProviderSupportsDirectLaunch(descriptor, product) && (hasLaunchUIContract || hasLegacyLaunchUI);
  }
  // Compatibility only while legacy one-key providers finish descriptor V2
  // migration. Registry-discovered providers never fall through this list.
  return slug === "hyperstack" || slug === "runpod";
}

function directLaunchReason(
  account: CloudWalletAccountPublic,
  snapshot?: CloudWalletBalanceSnapshot,
  providers: ProviderRegistryDescriptor[] = [],
) {
  if (!account.active) return "Inactive wallet";
  if (!account.has_api_key) return "Missing credentials";
  if (!supportsDirectLaunch(account.provider_slug, account.id, providers)) {
    const descriptor = providerDescriptorForSlug(providers, account.provider_slug);
    const product = providerDirectLaunchProductForAccount(account.id);
    return providerDirectLaunchBlocker(descriptor, product) || "Direct launch coming soon";
  }
  if (snapshot?.status === "error") return "Wallet probe needs attention";
  if (snapshot && snapshot.can_create_instance === false)
    return "Provider currently blocks new launches";
  return "";
}

function relTime(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function fmtUsd(value?: number) {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusTone(status: ProviderRun["status"]) {
  switch (status) {
    case "running":
      return "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]";
    case "launching":
    case "queued":
      return "border-primary/30 bg-primary/10 text-primary";
    case "stopping":
    case "suspending":
      return "border-amber-400/30 bg-amber-400/10 text-amber-300";
    case "failed":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    case "cleanup_pending":
      return "border-amber-400/30 bg-amber-400/10 text-amber-300";
    case "terminated":
    case "suspended":
    case "stopped":
      return "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]";
    default:
      return "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]";
  }
}

function workloadBadgeLabel(job: ProviderRun, runtimeResult: ManagedRuntimeResult | null) {
  if (runtimeResultIsVerificationOnly(runtimeResult)) return "Verification only";
  if (runtimeResult) return "Prompt completed";
  const runtimeState = managedRuntimeStatus(job);
  if (runtimeState === "ready") return "Prompt running";
  if (runtimeState === "starting") return "Starting runtime";
  if (runtimeState === "not_ready" || runtimeState === "prompt_failed") return "Prompt failed";
  if (providerDirectCleanupState(job) === "pending") return "Closing runtime";
  return jobStatusLabel(job.status);
}

function workloadBadgeTone(job: ProviderRun, runtimeResult: ManagedRuntimeResult | null) {
  if (runtimeResultIsVerificationOnly(runtimeResult))
    return "border-amber-400/30 bg-amber-400/10 text-amber-300";
  if (runtimeResult)
    return "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]";
  const runtimeState = managedRuntimeStatus(job);
  if (runtimeState === "ready" || runtimeState === "starting") {
    return "border-primary/30 bg-primary/10 text-primary";
  }
  if (runtimeState === "not_ready" || runtimeState === "prompt_failed") {
    return "border-destructive/30 bg-destructive/10 text-destructive";
  }
  return statusTone(job.status);
}

function jobStatusLabel(status: ProviderRun["status"]) {
  switch (status) {
    case "running":
      return "Running";
    case "launching":
      return "Launching";
    case "queued":
      return "Queued";
    case "cleanup_pending":
      return "Closing";
    case "stopping":
      return "Stopping";
    case "suspending":
      return "Suspending";
    case "suspended":
      return "Suspended";
    case "stopped":
      return "Stopped";
    case "terminated":
      return "Terminated";
    case "failed":
      return "Failed";
    default:
      return "Unknown";
  }
}

function providerStatusLabel(job: Pick<ProviderRun, "status" | "provider_status">) {
  const raw = String(job.provider_status || "").trim();
  if (raw === "GENRL_RUNTIME_STARTING") return "Starting runtime";
  if (raw === "GENRL_RUNTIME_READY") return "Runtime ready";
  if (raw === "GENRL_PROMPT_COMPLETED") return "Prompt completed";
  if (raw === "GENRL_PROMPT_FAILED") return "Prompt failed";
  if (raw === "GENRL_RUNTIME_NOT_READY") return "Runtime not ready";
  if (raw) return raw;
  return jobStatusLabel(job.status).toUpperCase();
}

function providerStateValue(job: Pick<ProviderRun, "status" | "provider_status">) {
  return providerStatusLabel(job).trim().toUpperCase();
}

function providerResourceFacts(job: ProviderRun | null | undefined) {
  return asRecord(job?.launch_request?.provider_resource);
}

function providerComputeStateLabel(job: ProviderRun | null | undefined) {
  const facts = providerResourceFacts(job);
  const cost = asRecord(facts?.cost);
  const computeState = String(cost?.compute_state || facts?.cost_state || "").trim();
  if (computeState) return providerTokenLabel(computeState);
  const powerState = String(facts?.power_state || "").trim();
  return powerState ? providerTokenLabel(powerState.replace(/^PowerState\//i, "")) : "Unknown";
}

function providerBillableResourcesLabel(job: ProviderRun | null | undefined) {
  const facts = providerResourceFacts(job);
  const cost = asRecord(facts?.cost);
  const components = providerCostComponentStates(cost?.components);
  const billable = components.filter((component) => component.billable === "yes");
  const unknown = components.filter((component) => component.billable === "unknown");
  const labels = [
    billable.length > 0
      ? billable.map((component) => providerTokenLabel(component.kind)).join(", ")
      : "",
    unknown.length > 0
      ? `Unconfirmed: ${unknown.map((component) => providerTokenLabel(component.kind)).join(", ")}`
      : "",
  ].filter(Boolean);
  return labels.join(" · ") || "None reported";
}

function providerAcceleratorLabel(job: ProviderRun | null | undefined) {
  const accelerator = asRecord(providerResourceFacts(job)?.accelerator);
  if (!accelerator) return String(job?.gpu || "").trim() || "—";
  const model = [accelerator.vendor, accelerator.model]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  const numerator = Number(accelerator.fraction_numerator || 0);
  const denominator = Number(accelerator.fraction_denominator || 0);
  const fraction = numerator > 0 && denominator > 0 ? `${numerator}/${denominator} GPU` : "";
  const vram = Number(accelerator.vram_gb || 0);
  return [model, fraction, vram > 0 ? `${vram} GB VRAM` : ""].filter(Boolean).join(" · ") || "—";
}

function isHyperstackJob(job: Pick<ProviderRun, "provider_slug"> | null | undefined) {
  return (
    String(job?.provider_slug || "")
      .trim()
      .toLowerCase() === "hyperstack"
  );
}

function canOpenProviderConsole(job: ProviderRun | null | undefined) {
  return isHyperstackJob(job);
}

function canStartProviderJob(
  job: ProviderRun | null | undefined,
  provider?: ProviderRegistryDescriptor,
) {
  if (!job || (!providerSupportsAction(provider, "start") && !isHyperstackJob(job))) return false;
  const state = providerStateValue(job);
  return ["SHUTOFF", "STOPPED", "DEALLOCATED", "PAUSED", "SUSPENDED"].includes(state) || ["stopped", "suspended"].includes(job.status);
}

function canStopProviderJob(
  job: ProviderRun | null | undefined,
  provider?: ProviderRegistryDescriptor,
) {
  if (!job || (!providerSupportsAction(provider, "stop") && !isHyperstackJob(job))) return false;
  const state = providerStateValue(job);
  return ["ACTIVE", "RUNNING", "READY"].includes(state) || job.status === "running";
}

function canHibernateProviderJob(
  job: ProviderRun | null | undefined,
  provider?: ProviderRegistryDescriptor,
) {
  if (!job || (!providerSupportsAction(provider, "hibernate") && !isHyperstackJob(job))) return false;
  const state = providerStateValue(job);
  return ["ACTIVE", "RUNNING", "READY"].includes(state) || job.status === "running";
}

function canRestoreProviderJob(
  job: ProviderRun | null | undefined,
  provider?: ProviderRegistryDescriptor,
) {
  if (!job || (!providerSupportsAction(provider, "hibernate-restore") && !isHyperstackJob(job))) return false;
  return providerStateValue(job) === "HIBERNATED";
}

function providerRestartAction(provider?: ProviderRegistryDescriptor): ProviderJobActionName {
  return providerSupportsAction(provider, "restart") ? "restart" : "hard-reboot";
}

function canRebootProviderJob(
  job: ProviderRun | null | undefined,
  provider?: ProviderRegistryDescriptor,
) {
  if (
    !job ||
    (!providerSupportsAction(provider, "restart") &&
      !providerSupportsAction(provider, "hard-reboot") &&
      !isHyperstackJob(job))
  )
    return false;
  const state = providerStateValue(job);
  return ["ACTIVE", "RUNNING", "READY", "ERROR"].includes(state) || job.status === "running";
}

function launchDefaults(account?: CloudWalletAccountPublic): LaunchFormState {
  return {
    account_id: String(account?.id || ""),
    provider_slug: "",
    name: "",
    workload_type: "text_generation",
    pack_mode: "auto",
    selected_packs: [],
    model_id: "deepseek-ai/DeepSeek-R1-Distill-Qwen-7B",
    use_custom_model: false,
    custom_model_ref: "",
    input_data_uri: "",
    prompt: "",
    session_mode: "one_shot",
    idle_timeout_seconds: 1800,
    max_ttl_seconds: 14400,
    auto_stop_idle: true,
    save_run_context: false,
    routing_preference: "cheapest",
    max_price_credits: 5,
    estimated_minutes: 8,
    environment_name: "",
    flavor_name: "",
    image_name: "",
    assign_public_ip: true,
    key_name: "",
    gpu_type_id: "",
    gpu_count: 1,
    cloud_type: "COMMUNITY",
    docker_image: "vllm/vllm-openai:latest",
    start_command: "sleep infinity",
    container_disk_gb: 20,
    volume_gb: 10,
    port_spec: "8000/http",
    restore_context: null,
  };
}

function isHyperstackCatalog(
  catalog: ProviderLaunchCatalog | undefined,
): catalog is HyperstackProviderLaunchCatalog {
  return catalog?.provider_slug === "hyperstack";
}

function isRunPodCatalog(
  catalog: ProviderLaunchCatalog | undefined,
): catalog is RunPodProviderLaunchCatalog {
  return catalog?.provider_slug === "runpod";
}

function hyperstackRegionFromEnvironment(value?: string) {
  const env = String(value || "").trim();
  if (!env) return "";
  if (/^default[-_]/i.test(env))
    return env
      .replace(/^default[-_]/i, "")
      .trim()
      .toUpperCase();
  return env.toUpperCase();
}

function hyperstackEnvironmentRegion(
  environments: HyperstackCatalogEnvironment[],
  selectedName: string,
) {
  const match = environments.find((entry) => entry.name === selectedName);
  return (
    String(match?.region || "")
      .trim()
      .toUpperCase() || hyperstackRegionFromEnvironment(selectedName)
  );
}

function hyperstackFlavorHasStock(row: HyperstackCatalogFlavor | undefined) {
  if (!row) return false;
  if (row.stock_available === undefined) return true;
  return row.stock_available > 0;
}

function hyperstackKeypairsForEnvironment(
  keypairs: HyperstackCatalogKeypair[],
  environmentName: string,
) {
  const envName = String(environmentName || "").trim();
  if (!envName) return keypairs;
  const hasScopedKeypairs = keypairs.some((row) => !!String(row.environment_name || "").trim());
  const exact = keypairs.filter((row) => String(row.environment_name || "").trim() === envName);
  if (exact.length > 0) return exact;
  return hasScopedKeypairs ? [] : keypairs;
}

function formatStockLabel(value?: string) {
  const stock = String(value || "").trim();
  if (!stock) return "";
  return `${stock} stock`;
}

function normalizeHardwareKey(value: string) {
  return String(value || "")
    .toUpperCase()
    .replace(/NVIDIA|GEFORCE|TESLA|RTX|GPU/g, "")
    .replace(/[^A-Z0-9]/g, "")
    .trim();
}

function compactUnique(values: Array<string | undefined>) {
  const seen = new Set<string>();
  const out: string[] = [];
  values.forEach((value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed || seen.has(trimmed.toLowerCase())) return;
    seen.add(trimmed.toLowerCase());
    out.push(trimmed);
  });
  return out.sort((a, b) => a.localeCompare(b));
}

function hardwareOptionDetail(option: {
  stockAvailable?: number;
  providerCount?: number;
  providers?: string[];
  bestPricePerHour?: number;
  originalPricePerHour?: number;
  discountPercent?: number;
  fromProAccount?: boolean;
  source?: AvailableHardwareOption["source"];
}) {
  const parts: string[] = [];
  const stock = Number(option.stockAvailable || 0);
  if (stock > 0) parts.push(`${stock} live`);
  const providerCount = Number(option.providerCount || option.providers?.length || 0);
  if (providerCount > 0) parts.push(`${providerCount} provider${providerCount === 1 ? "" : "s"}`);
  const currentPrice = formatHostedHardwareHourlyPrice(option.bestPricePerHour);
  const originalPrice = formatHostedHardwareHourlyPrice(option.originalPricePerHour);
  const discountPercent = Math.max(0, Math.round(Number(option.discountPercent || 0)));
  const discounted = Boolean(
    option.fromProAccount &&
      currentPrice &&
      originalPrice &&
      Number(option.originalPricePerHour || 0) > Number(option.bestPricePerHour || 0) &&
      discountPercent > 0,
  );
  if (discounted) {
    parts.push(`${originalPrice} → ${currentPrice} · Pro account · ${discountPercent}% off`);
  } else if (currentPrice) {
    parts.push(`from ${currentPrice}`);
  }
  if (!parts.length && option.source === "fallback") return "Default hardware preference";
  return parts.join(" · ") || "Live capacity";
}

function formatHostedHardwareHourlyPrice(value?: number) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  const maximumFractionDigits = amount < 1 ? 3 : amount < 10 ? 2 : 1;
  return `${amount.toLocaleString(undefined, { maximumFractionDigits })} UC/hr`;
}

function runPodGPUHourlyCost(gpuType: RunPodCatalogGPUType) {
  const prices = [gpuType.community, gpuType.secure]
    .filter((price): price is RunPodCatalogPrice => runPodCatalogPriceLaunchable(price))
    .map((price) => Number(price.uninterruptable_price))
    .filter((price) => Number.isFinite(price) && price > 0);
  return prices.length > 0 ? Math.min(...prices) : undefined;
}

function runPodCatalogPriceLaunchable(price?: RunPodCatalogPrice) {
  if (!price || !(Number(price.uninterruptable_price) > 0)) return false;
  if ((price.available_gpu_counts || []).some((count) => Number(count) > 0)) return true;
  if (Number(price.max_unreserved_gpu_count || 0) > 0) return true;
  const stock = String(price.stock_status || "").trim().toLowerCase();
  return Boolean(stock && !/(out|unavailable|none|zero)/.test(stock));
}

function runPodGPUTypeLaunchable(gpuType: RunPodCatalogGPUType) {
  return (
    (gpuType.community_cloud && runPodCatalogPriceLaunchable(gpuType.community)) ||
    (gpuType.secure_cloud && runPodCatalogPriceLaunchable(gpuType.secure))
  );
}

function hardwareDisplayLabel(label: string, gpuCount: number) {
  const cleanLabel = String(label || "").trim();
  const count = Math.max(1, Math.round(Number(gpuCount || 1)));
  return count > 1 ? `${cleanLabel} x${count}` : cleanLabel;
}

function mapProviderHardwareOptions(rows: ProviderJobHardwareOptionPayload[] | undefined) {
  return (Array.isArray(rows) ? rows : [])
    .map((row): AvailableHardwareOption | null => {
      const rawLabel = String(row.hardware_label || row.hardware_id || "").trim();
      if (!rawLabel) return null;
      const gpuCount = Math.max(1, Math.round(Number(row.gpu_count || 1)));
      const providers = compactUnique(row.providers || []);
      const bestBuyerPrice = Number(row.best_buyer_price_micros || 0) / MICRO || 0;
      const originalBuyerPrice = Number(row.original_buyer_price_micros || 0) / MICRO || 0;
      const bestPricePerHour =
        (bestBuyerPrice || Number(row.best_price_per_hour || 0) || 0) * gpuCount;
      const originalPricePerHour = originalBuyerPrice * gpuCount;
      const option: AvailableHardwareOption = {
        value: rawLabel,
        label: hardwareDisplayLabel(rawLabel, gpuCount),
        detail: "",
        gpuCount,
        stockAvailable: Math.max(0, Math.round(Number(row.stock_available || 0))),
        providerCount: Math.max(0, Math.round(Number(row.provider_count || providers.length || 0))),
        providers,
        bestPricePerHour: bestPricePerHour || undefined,
        originalPricePerHour: originalPricePerHour || undefined,
        discountPercent: Math.max(0, Math.round(Number(row.discount_percent || 0))),
        fromProAccount: Boolean(row.from_pro_account),
        source: "capacity_table",
      };
      option.detail = hardwareOptionDetail(option);
      return option;
    })
    .filter((option): option is AvailableHardwareOption => !!option);
}

function marketOfferHardwareLabel(offer: MarketOffer) {
  return String(offer.gpu || offer.gpu_type || offer.flavor_name || offer.option_id || "").trim();
}

function mapMarketOffersToHardwareOptions(offers: MarketOffer[] | undefined) {
  type Accumulator = {
    label: string;
    gpuCount: number;
    stockAvailable: number;
    providerNames: string[];
    bestPricePerHour?: number;
  };
  const byHardware = new Map<string, Accumulator>();
  (Array.isArray(offers) ? offers : []).forEach((offer) => {
    const rawLabel = marketOfferHardwareLabel(offer);
    if (!rawLabel) return;
    const gpuCount = Math.max(1, Math.round(Number(offer.gpu_count || 1)));
    const stock = Math.max(0, Math.round(Number(offer.stock_available || 0)));
    if (stock <= 0) return;
    const key = `${normalizeHardwareKey(rawLabel)}:${gpuCount}`;
    const current = byHardware.get(key) || {
      label: rawLabel,
      gpuCount,
      stockAvailable: 0,
      providerNames: [],
      bestPricePerHour: undefined,
    };
    current.stockAvailable += stock;
    current.providerNames.push(offer.provider_name || offer.provider_slug || offer.source || "");
    const buyerPrice = Number(offer.buyer_unit_price_micros || 0) / MICRO || 0;
    const price = buyerPrice || Number(offer.price_per_hour || 0);
    if (price > 0 && (!current.bestPricePerHour || price < current.bestPricePerHour)) {
      current.bestPricePerHour = price;
    }
    byHardware.set(key, current);
  });
  return [...byHardware.values()]
    .map((row): AvailableHardwareOption => {
      const providers = compactUnique(row.providerNames);
      const option: AvailableHardwareOption = {
        value: row.label,
        label: hardwareDisplayLabel(row.label, row.gpuCount),
        detail: "",
        gpuCount: row.gpuCount,
        stockAvailable: row.stockAvailable,
        providerCount: providers.length,
        providers,
        bestPricePerHour: row.bestPricePerHour,
        originalPricePerHour: undefined,
        discountPercent: 0,
        fromProAccount: false,
        source: "market_offers",
      };
      option.detail = hardwareOptionDetail(option);
      return option;
    })
    .sort((a, b) => {
      if (a.bestPricePerHour && b.bestPricePerHour && a.bestPricePerHour !== b.bestPricePerHour) {
        return a.bestPricePerHour - b.bestPricePerHour;
      }
      return a.label.localeCompare(b.label);
    });
}

function fallbackHardwareOptions(): AvailableHardwareOption[] {
  return [];
}

async function readAvailableHardwareOptions(providerSlug = "") {
  const providerScoped = Boolean(String(providerSlug || "").trim());
  try {
    const payload = await apiRequest<ProviderJobHardwarePayload>(
      providerScoped
        ? walletManagedProviderHardwareRequestPath(providerSlug)
        : WALLET_PROVIDER_HARDWARE_API_PATH,
      {
        method: "GET",
        timeoutMs: 36000,
      },
    );
    const options = mapProviderHardwareOptions(
      walletProviderHardwareOptionsFromPayload<ProviderJobHardwareOptionPayload>(payload),
    );
    if (options.length > 0) return options;
  } catch (error) {
    // Aggregate discovery can fall back to public offers. A provider-scoped
    // request must fail closed rather than reintroducing aggregate price/stock.
    if (providerScoped) throw error;
  }

  if (providerScoped) return [];

  try {
    const payload = await apiRequest<MarketOffersPayload>("/api/v1/market/offers", {
      method: "GET",
      timeoutMs: 20000,
    });
    const options = mapMarketOffersToHardwareOptions(payload.offers);
    if (options.length > 0) return options;
  } catch {
    // Keep Auto hardware usable without inventing specific GPU stock.
  }
  return fallbackHardwareOptions();
}

function runPodCloudTypeOptions(gpuType?: RunPodCatalogGPUType): string[] {
  if (!gpuType) return ["COMMUNITY", "SECURE"];
  const out: string[] = [];
  if (gpuType.community_cloud) out.push("COMMUNITY");
  if (gpuType.secure_cloud) out.push("SECURE");
  return out.length > 0 ? out : ["COMMUNITY", "SECURE"];
}

function runPodCloudLabel(cloudType: string) {
  return cloudType === "SECURE" ? "Secure cloud" : "Community cloud";
}

function runPodPriceForCloud(gpuType: RunPodCatalogGPUType | undefined, cloudType: string) {
  if (!gpuType) return undefined;
  return cloudType === "SECURE" ? gpuType.secure : gpuType.community;
}

function runPodAvailableGpuCounts(
  gpuType: RunPodCatalogGPUType | undefined,
  cloudType: string,
): number[] {
  const row = runPodPriceForCloud(gpuType, cloudType);
  const counts = Array.isArray(row?.available_gpu_counts) ? row?.available_gpu_counts || [] : [];
  const unique = [
    ...new Set(
      counts.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0),
    ),
  ];
  return unique.sort((a, b) => a - b);
}

function syncFormWithCatalog(
  current: LaunchFormState,
  account: CloudWalletAccountPublic,
  catalog: ProviderLaunchCatalog,
): LaunchFormState {
  let next = { ...current };
  let changed = false;

  if (isHyperstackCatalog(catalog)) {
    const environmentNames = new Set(catalog.environments.map((row) => row.name));
    const choosePreferredEnvironment = () => {
      const candidates = catalog.environments.map((environment) => {
        const region = hyperstackEnvironmentRegion(catalog.environments, environment.name);
        const flavorOptions = catalog.flavors.filter(
          (row) => !region || !row.region || row.region.toUpperCase() === region,
        );
        const inStockFlavorCount = flavorOptions.filter((row) =>
          hyperstackFlavorHasStock(row),
        ).length;
        const keypairCount = hyperstackKeypairsForEnvironment(
          catalog.keypairs,
          environment.name,
        ).length;
        return {
          environment,
          inStockFlavorCount,
          keypairCount,
        };
      });
      return (
        candidates.find((row) => row.inStockFlavorCount > 0 && row.keypairCount > 0)?.environment ||
        candidates.find((row) => row.inStockFlavorCount > 0)?.environment ||
        candidates.find((row) => row.keypairCount > 0)?.environment ||
        catalog.environments[0]
      );
    };
    if (
      catalog.environments.length > 0 &&
      (!next.environment_name || !environmentNames.has(next.environment_name))
    ) {
      next.environment_name = choosePreferredEnvironment()?.name || catalog.environments[0].name;
      changed = true;
    }

    const region = hyperstackEnvironmentRegion(catalog.environments, next.environment_name);
    const flavorOptions = catalog.flavors.filter(
      (row) => !region || !row.region || row.region.toUpperCase() === region,
    );
    const inStockFlavorOptions = flavorOptions.filter((row) => hyperstackFlavorHasStock(row));
    const selectableFlavorOptions =
      inStockFlavorOptions.length > 0 ? inStockFlavorOptions : flavorOptions;
    const imageOptions = catalog.images.filter(
      (row) => !region || !row.region || row.region.toUpperCase() === region,
    );
    const keypairOptions = hyperstackKeypairsForEnvironment(
      catalog.keypairs,
      next.environment_name,
    );

    if (selectableFlavorOptions.length > 0) {
      const flavorNames = new Set(selectableFlavorOptions.map((row) => row.name));
      if (!next.flavor_name || !flavorNames.has(next.flavor_name)) {
        next.flavor_name = selectableFlavorOptions[0].name;
        changed = true;
      }
    }

    if (imageOptions.length > 0) {
      const imageNames = new Set(imageOptions.map((row) => row.name));
      if (!next.image_name || !imageNames.has(next.image_name)) {
        next.image_name = imageOptions[0].name;
        changed = true;
      }
    }

    if (keypairOptions.length > 0) {
      const keypairNames = new Set(keypairOptions.map((row) => row.name));
      if (!next.key_name || !keypairNames.has(next.key_name)) {
        next.key_name = keypairOptions[0].name;
        changed = true;
      }
    } else if (next.key_name) {
      next.key_name = "";
      changed = true;
    }

    return changed ? next : current;
  }

  if (isRunPodCatalog(catalog)) {
    if (!next.docker_image) {
      next.docker_image = catalog.defaults.docker_image;
      changed = true;
    }
    if (!next.start_command) {
      next.start_command = catalog.defaults.start_command;
      changed = true;
    }
    if (!next.port_spec) {
      next.port_spec = catalog.defaults.port_spec;
      changed = true;
    }
    if (!next.container_disk_gb) {
      next.container_disk_gb = catalog.defaults.container_disk_gb;
      changed = true;
    }
    if (!next.volume_gb) {
      next.volume_gb = catalog.defaults.volume_gb;
      changed = true;
    }

    const gpuIDs = new Set(catalog.gpu_types.map((row) => row.id));
    if (catalog.gpu_types.length > 0 && (!next.gpu_type_id || !gpuIDs.has(next.gpu_type_id))) {
      next.gpu_type_id = catalog.gpu_types[0].id;
      changed = true;
    }
    const gpuType = catalog.gpu_types.find((row) => row.id === next.gpu_type_id);
    const allowedCloudTypes = runPodCloudTypeOptions(gpuType);
    if (!allowedCloudTypes.includes(next.cloud_type)) {
      next.cloud_type = allowedCloudTypes[0] || "COMMUNITY";
      changed = true;
    }
    const allowedCounts = runPodAvailableGpuCounts(gpuType, next.cloud_type);
    if (allowedCounts.length > 0 && !allowedCounts.includes(next.gpu_count)) {
      next.gpu_count = allowedCounts[0];
      changed = true;
    }
  }

  return changed ? next : current;
}

function providerTokenLabel(value: string) {
  return String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function registryLaunchFieldOptions(
  provider: ProviderRegistryDescriptor,
  field: ProviderRegistryInputField,
  catalog: ProviderLaunchCatalog | undefined,
  values: ProviderInputValues,
): ProviderCatalogOption[] {
  if (field.key === "access_mode") {
    return providerAccessModeOptions(provider);
  }
  return providerCatalogInputOptions(
    field,
    catalog as Record<string, unknown> | undefined,
    values,
  );
}

function syncRegistryLaunchInputs(
  provider: ProviderRegistryDescriptor,
  catalog: ProviderLaunchCatalog | undefined,
  current: ProviderInputValues,
) {
  const next = providerInputInitialValues(provider.launch_inputs, current);
  for (const field of provider.launch_inputs) {
    const options = registryLaunchFieldOptions(provider, field, catalog, next);
    if (options.length === 0) continue;
    const currentValue = String(next[field.key] ?? "").trim();
    const currentOption = options.find((option) => option.value === currentValue && !option.disabled);
    if (currentOption) continue;
    if (!field.required && !currentValue) continue;
    const fallback = options.find((option) => !option.disabled);
    if (fallback) next[field.key] = providerInputValue(field, fallback.value);
  }
  return next;
}

export function HostedProviderJobsPage() {
  const [cachedSnapshot] = useState(() =>
    readPageCache<WorkloadsPageCache>(WORKLOADS_PAGE_CACHE_KEY, WORKLOADS_PAGE_CACHE_MAX_AGE_MS),
  );
  const [accounts, setAccounts] = useState<LaunchAccount[]>(
    () => cachedSnapshot?.data.accounts ?? [],
  );
  const [jobs, setJobs] = useState<ProviderRun[]>(() => cachedSnapshot?.data.jobs ?? []);
  const [runtimeSessions, setRuntimeSessions] = useState<RuntimeSession[]>(
    () => cachedSnapshot?.data.runtimeSessions ?? [],
  );
  const [catalogs, setCatalogs] = useState<Record<string, ProviderLaunchCatalog>>(
    () => cachedSnapshot?.data.catalogs ?? {},
  );
  const [hardwareOptions, setHardwareOptions] = useState<AvailableHardwareOption[]>(
    () => cachedSnapshot?.data.hardwareOptions ?? [],
  );
  const [managedProviderHardware, setManagedProviderHardware] = useState<{
    providerSlug: string;
    options: AvailableHardwareOption[];
    loading: boolean;
    error: string;
  }>({ providerSlug: "", options: [], loading: false, error: "" });
  const [providerDescriptors, setProviderDescriptors] = useState<ProviderRegistryDescriptor[]>(
    () => cachedSnapshot?.data.providerDescriptors ?? [],
  );
  const [providerInputValues, setProviderInputValues] = useState<ProviderInputValues>({});
  const [catalogLoadingByID, setCatalogLoadingByID] = useState<Record<string, boolean>>({});
  const [catalogErrorsByID, setCatalogErrorsByID] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(() => !cachedSnapshot);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState(() => cachedSnapshot?.updatedAt ?? "");
  const [launching, setLaunching] = useState(false);
  const [artifactStage, setArtifactStage] = useState("");
  const [dataFiles, setDataFiles] = useState<File[]>([]);
  const [modelWeightsFile, setModelWeightsFile] = useState<File | null>(null);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [form, setForm] = useState<LaunchFormState>(launchDefaults());
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedJobID, setSelectedJobID] = useState("");
  const [detailTab, setDetailTab] = useState<"direct" | "ssh">("direct");
  const [probingJob, setProbingJob] = useState(false);
  const [jobProbe, setJobProbe] = useState<ProviderJobProbe | null>(null);
  const [openingConsole, setOpeningConsole] = useState(false);
  const [consoleResult, setConsoleResult] = useState<ProviderJobConsoleResult | null>(null);
  const [providerActionBusy, setProviderActionBusy] = useState<ProviderJobActionName | "">("");
  const [inferencePrompt, setInferencePrompt] = useState(
    "Write a one-sentence health check for this runtime.",
  );
  const [inferenceModel, setInferenceModel] = useState("");
  const [inferring, setInferring] = useState(false);
  const [inferenceResult, setInferenceResult] = useState<ProviderJobInferenceResult | null>(null);
  const [sessionInputs, setSessionInputs] = useState<Record<string, string>>({});
  const [sessionTurnsByID, setSessionTurnsByID] = useState<Record<string, RuntimeSessionTurn[]>>(
    {},
  );
  const [sessionBusyByID, setSessionBusyByID] = useState<Record<string, boolean>>({});

  async function refresh(options?: { silent?: boolean }) {
    const hasVisibleData = accounts.length > 0 || jobs.length > 0;
    if (options?.silent || hasVisibleData) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [
        publicAccounts,
        summary,
        jobsPayload,
        sessionsPayload,
        nextHardwareOptions,
        registryPayload,
      ] =
        await Promise.all([
          readCloudWalletAccountsPublic().catch(() => []),
          apiRequest<CloudWalletSummaryResponse>("/api/wallet/summary", {
            method: "POST",
            body: {},
            timeoutMs: 32000,
          }).catch(() => null),
          apiRequest<{ jobs?: ProviderRun[] }>("/api/wallet/accounts?action=provider_jobs", {
            method: "GET",
            timeoutMs: 32000,
          }),
          apiRequest<{ sessions?: RuntimeSession[] }>("/api/v1/runtime/sessions", {
            method: "GET",
            timeoutMs: 32000,
          }).catch(() => ({ sessions: [] })),
          readAvailableHardwareOptions(),
          // This is a browser discovery request; use the Pro-safe credential
          // projection even though this page consumes only launch metadata.
          readProviderRegistry("pro").catch(() => null),
        ]);
      const nextProviderDescriptors = registryPayload?.providers || providerDescriptors;
      const snapshotByID = new Map(
        Array.isArray(summary?.accounts)
          ? summary.accounts.map((snapshot) => [String(snapshot.account_id || "").trim(), snapshot])
          : [],
      );
      const nextAccounts = publicAccounts
        .map((account) => {
          const snapshot = snapshotByID.get(String(account.id || "").trim());
          const launchReason = directLaunchReason(
            account,
            snapshot,
            nextProviderDescriptors,
          );
          return {
            ...account,
            snapshot,
            directLaunch: !launchReason,
            launchReason,
          };
        })
        .sort((a, b) => {
          if (!!a.directLaunch !== !!b.directLaunch) return a.directLaunch ? -1 : 1;
          return a.display_name.localeCompare(b.display_name);
        });
      const nextJobs = Array.isArray(jobsPayload.jobs) ? jobsPayload.jobs : [];
      const nextRuntimeSessions = Array.isArray(sessionsPayload.sessions)
        ? sessionsPayload.sessions
        : [];
      const nextSessionTurnsByID = await loadRuntimeSessionTurns(nextRuntimeSessions);
      setAccounts(nextAccounts);
      setJobs(nextJobs);
      setRuntimeSessions(nextRuntimeSessions);
      if (Object.keys(nextSessionTurnsByID).length > 0) {
        setSessionTurnsByID((current) => ({ ...current, ...nextSessionTurnsByID }));
      }
      setHardwareOptions(nextHardwareOptions);
      setProviderDescriptors(nextProviderDescriptors);
      const record = writePageCache<WorkloadsPageCache>(WORKLOADS_PAGE_CACHE_KEY, {
        accounts: nextAccounts,
        jobs: nextJobs,
        runtimeSessions: nextRuntimeSessions,
        catalogs,
        hardwareOptions: nextHardwareOptions,
        providerDescriptors: nextProviderDescriptors,
      });
      if (record) setCacheUpdatedAt(record.updatedAt);
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail || "Could not load provider jobs."
          : "Could not load provider jobs.";
      if (hasVisibleData) {
        toast.warning("Showing cached workload data while refresh catches up.");
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void refresh({ silent: true });
    }, REFRESH_MS);
    return () => window.clearInterval(interval);
  }, []);

  async function loadRuntimeSessionTurns(sessions: RuntimeSession[]) {
    const activeWithTurns = sessions
      .filter((session) => runtimeSessionIsActive(session))
      .filter(
        (session) =>
          session.turn_count > 0 || session.last_turn_id || session.state === "running_input",
      )
      .slice(0, 12);
    const entries = await Promise.all(
      activeWithTurns.map(async (session) => {
        try {
          const detail = await apiRequest<RuntimeSessionDetailPayload>(
            `/api/v1/runtime/sessions/${session.id}`,
            {
              method: "GET",
              timeoutMs: 20000,
              cache: "no-store",
            },
          );
          return [
            session.id,
            Array.isArray(detail.turns) ? detail.turns : ([] as RuntimeSessionTurn[]),
          ] as const;
        } catch {
          return [session.id, [] as RuntimeSessionTurn[]] as const;
        }
      }),
    );
    return entries.reduce<Record<string, RuntimeSessionTurn[]>>((acc, [sessionID, turns]) => {
      if (turns.length > 0) acc[sessionID] = turns;
      return acc;
    }, {});
  }

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === form.account_id),
    [accounts, form.account_id],
  );
  const directLaunchAccounts = useMemo(
    () => accounts.filter((account) => account.directLaunch),
    [accounts],
  );
  const selectedAccountDescriptor = useMemo(
    () => providerDescriptorForSlug(providerDescriptors, selectedAccount?.provider_slug || ""),
    [providerDescriptors, selectedAccount?.provider_slug],
  );
  const directModelFreeLaunch = Boolean(
    selectedAccount &&
      (providerDirectLaunchIsModelFree(
        selectedAccount.id,
        selectedAccountDescriptor,
        providerInputValues,
      ) || String(selectedAccount.provider_slug || "").trim().toLowerCase() === "hyperstack"),
  );
  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedJobID) || null,
    [jobs, selectedJobID],
  );
  const selectedJobDescriptor = useMemo(
    () => providerDescriptorForSlug(providerDescriptors, selectedJob?.provider_slug || ""),
    [providerDescriptors, selectedJob?.provider_slug],
  );
  const selectedCatalog = selectedAccount ? catalogs[selectedAccount.id] : undefined;
  const selectedCatalogLoading = !!(selectedAccount && catalogLoadingByID[selectedAccount.id]);
  const selectedCatalogError = selectedAccount ? catalogErrorsByID[selectedAccount.id] : "";
  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => workloadSortTime(b) - workloadSortTime(a)),
    [jobs],
  );
  const visibleSortedJobs = useMemo(
    () => sortedJobs.filter((job) => !isPersistentRuntimeContainerJob(job)),
    [sortedJobs],
  );
  const visibleJobCostBandByID = useMemo(() => {
    const bands = assignHardwareCostBands(visibleSortedJobs, workloadHourlyRateMicros);
    return new Map(
      visibleSortedJobs.map((job, index) => [job.id, bands[index] || "Mid cost"] as const),
    );
  }, [visibleSortedJobs]);

  function upsertJob(next: ProviderRun) {
    setJobs((current) => {
      const index = current.findIndex((job) => job.id === next.id);
      if (index < 0) {
        const merged = [next, ...current];
        const record = writePageCache<WorkloadsPageCache>(WORKLOADS_PAGE_CACHE_KEY, {
          accounts,
          jobs: merged,
          runtimeSessions,
          catalogs,
          hardwareOptions,
          providerDescriptors,
        });
        if (record) window.setTimeout(() => setCacheUpdatedAt(record.updatedAt), 0);
        return merged;
      }
      const copy = [...current];
      copy[index] = next;
      const record = writePageCache<WorkloadsPageCache>(WORKLOADS_PAGE_CACHE_KEY, {
        accounts,
        jobs: copy,
        runtimeSessions,
        catalogs,
        hardwareOptions,
        providerDescriptors,
      });
      if (record) window.setTimeout(() => setCacheUpdatedAt(record.updatedAt), 0);
      return copy;
    });
  }

  function upsertRuntimeSession(next: RuntimeSession, turns?: RuntimeSessionTurn[]) {
    setRuntimeSessions((current) => {
      const index = current.findIndex((session) => session.id === next.id);
      const merged =
        index < 0 ? [next, ...current] : current.map((row) => (row.id === next.id ? next : row));
      const record = writePageCache<WorkloadsPageCache>(WORKLOADS_PAGE_CACHE_KEY, {
        accounts,
        jobs,
        runtimeSessions: merged,
        catalogs,
        hardwareOptions,
        providerDescriptors,
      });
      if (record) window.setTimeout(() => setCacheUpdatedAt(record.updatedAt), 0);
      return merged;
    });
    if (turns) {
      setSessionTurnsByID((current) => ({ ...current, [next.id]: turns }));
    }
  }

  function chooseHardwarePreference(value: string) {
    const selected = hardwareOptions.find(
      (option) =>
        option.value === value ||
        normalizeHardwareKey(option.value) === normalizeHardwareKey(value),
    );
    setForm((current) => ({
      ...current,
      gpu_type_id: value,
      gpu_count: value ? selected?.gpuCount || current.gpu_count || 1 : 1,
    }));
  }

  function chooseManagedProvider(providerSlug: string) {
    setForm((current) => ({
      ...current,
      provider_slug: providerSlug,
      gpu_type_id: current.provider_slug === providerSlug ? current.gpu_type_id : "",
      gpu_count: current.provider_slug === providerSlug ? current.gpu_count : 1,
    }));
  }

  function openJobDetail(job: ProviderRun) {
    setSelectedJobID(job.id);
    setDetailTab("direct");
    setJobProbe(null);
    setConsoleResult(null);
    setInferenceResult(null);
    setInferenceModel("");
    setInferencePrompt("Write a one-sentence health check for this runtime.");
    setDetailOpen(true);
  }

  async function copyRuntimeOutput(output: string) {
    try {
      await navigator.clipboard.writeText(output);
      toast.success("Output copied");
    } catch {
      toast.error("Could not copy output.");
    }
  }

  async function handleUseRunSnapshot(snapshotID: string) {
    const id = String(snapshotID || "").trim();
    if (!id) return;
    try {
      const response = await apiRequest<RunSnapshotRerunDraftResponse>(
        `/api/v1/run-snapshots/${encodeURIComponent(id)}/rerun-draft`,
        {
          method: "POST",
          body: {},
          timeoutMs: 20000,
        },
      );
      const draft = asRecord(response.launch_request) || {};
      setForm((current) => ({
        ...current,
        account_id: String(draft.account_id || ""),
        provider_slug: String(draft.provider_slug || current.provider_slug || ""),
        name: "",
        workload_type: String(draft.workload_type || current.workload_type || "text_generation"),
        model_id: String(draft.model_id || current.model_id || "genrl-recommended"),
        prompt: String(draft.prompt || ""),
        session_mode:
          String(draft.session_mode || "one_shot") === "persistent" ? "persistent" : "one_shot",
        save_run_context: draft.save_run_context !== false,
        routing_preference: String(draft.routing_preference || current.routing_preference || "cheapest"),
        environment_name: String(draft.environment_name || current.environment_name || ""),
        gpu_type_id: String(draft.gpu_type_id || current.gpu_type_id || ""),
        restore_context: asRecord(draft.restore_context),
      }));
      setLaunchOpen(true);
      toast.success("Snapshot context loaded");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load snapshot context.");
    }
  }

  async function handleOpenConsole(job: ProviderRun) {
    if (!job) return;
    const popup =
      typeof window !== "undefined" ? window.open("", "_blank", "noopener,noreferrer") : null;
    setOpeningConsole(true);
    try {
      const payload = await apiRequest<{ job?: ProviderRun; console: ProviderJobConsoleResult }>(
        "/api/wallet/accounts?action=provider_job_console",
        {
          method: "POST",
          body: { id: job.id },
          timeoutMs: 30000,
        },
      );
      if (payload.job) upsertJob(payload.job);
      setConsoleResult(payload.console);
      if (popup) {
        popup.location.href = payload.console.url;
      } else if (typeof window !== "undefined") {
        window.open(payload.console.url, "_blank", "noopener,noreferrer");
      }
      toast.success("Opening VM console");
    } catch (error) {
      if (popup) popup.close();
      const message =
        error instanceof APIError
          ? error.detail || "Could not open the provider console."
          : "Could not open the provider console.";
      toast.error(message);
    } finally {
      setOpeningConsole(false);
    }
  }

  async function handleProviderAction(job: ProviderRun, action: ProviderJobActionName) {
    if (!job) return;
    setProviderActionBusy(action);
    try {
      const payload = await apiRequest<ProviderJobActionResult>(
        "/api/wallet/accounts?action=provider_job_action",
        {
          method: "POST",
          body: providerJobActionPayload(job.id, action),
          timeoutMs: 30000,
        },
      );
      upsertJob(payload.job);
      toast.success(payload.message);
      if (detailOpen && selectedJobID === job.id) {
        setJobProbe(null);
      }
      void refresh({ silent: true });
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail || "Could not run the provider action."
          : "Could not run the provider action.";
      toast.error(message);
    } finally {
      setProviderActionBusy("");
    }
  }

  async function handleProbeJob(jobID: string, options?: { silent?: boolean }) {
    if (!jobID) return;
    if (!options?.silent) setProbingJob(true);
    try {
      const payload = await apiRequest<{ job?: ProviderRun; probe: ProviderJobProbe }>(
        "/api/wallet/accounts?action=provider_job_runtime_probe",
        {
          method: "POST",
          body: { id: jobID },
          timeoutMs: 30000,
        },
      );
      if (payload.job) upsertJob(payload.job);
      setJobProbe(payload.probe);
      if (Array.isArray(payload.probe.models) && payload.probe.models.length > 0) {
        setInferenceModel((current) => current || payload.probe.models?.[0] || "");
      }
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail || "Could not inspect this provider runtime."
          : "Could not inspect this provider runtime.";
      toast.error(message);
    } finally {
      if (!options?.silent) setProbingJob(false);
    }
  }

  async function handleRunInference() {
    if (!selectedJob) {
      toast.error("Choose a running provider job first.");
      return;
    }
    if (isManagedWalletJob(selectedJob)) {
      toast.info(
        "GENRL is already running this workload automatically. Refresh the job to see the result.",
      );
      return;
    }
    const prompt = String(inferencePrompt || "").trim();
    if (!prompt) {
      toast.error("Add a prompt before running inference.");
      return;
    }
    setInferring(true);
    try {
      const payload = await apiRequest<{ job?: ProviderRun; result: ProviderJobInferenceResult }>(
        "/api/wallet/accounts?action=provider_job_inference",
        {
          method: "POST",
          body: {
            id: selectedJob.id,
            model: inferenceModel,
            prompt,
          },
          timeoutMs: 45000,
        },
      );
      if (payload.job) upsertJob(payload.job);
      setInferenceResult(payload.result);
      toast.success("Inference completed");
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail || "Could not run inference on this provider runtime."
          : "Could not run inference on this provider runtime.";
      toast.error(message);
    } finally {
      setInferring(false);
    }
  }

  async function loadCatalog(account: LaunchAccount, options?: { force?: boolean }) {
    if (!account.directLaunch) return;
    if (!options?.force && (catalogs[account.id] || catalogLoadingByID[account.id])) return;
    setCatalogLoadingByID((current) => ({ ...current, [account.id]: true }));
    setCatalogErrorsByID((current) => {
      const next = { ...current };
      delete next[account.id];
      return next;
    });
    try {
      const payload = await apiRequest<{ catalog: ProviderLaunchCatalog }>(
        `/api/wallet/accounts?action=provider_job_catalog&account_id=${encodeURIComponent(account.id)}`,
        {
          method: "GET",
          timeoutMs: 45_000,
        },
      );
      if (!payload?.catalog) {
        throw new Error("No provider launch options came back.");
      }
      setCatalogs((current) => {
        const next = { ...current, [account.id]: payload.catalog };
        const record = writePageCache<WorkloadsPageCache>(WORKLOADS_PAGE_CACHE_KEY, {
          accounts,
          jobs,
          catalogs: next,
          hardwareOptions,
          providerDescriptors,
        });
        if (record) window.setTimeout(() => setCacheUpdatedAt(record.updatedAt), 0);
        return next;
      });
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail || "Could not load provider launch options."
          : error instanceof Error
            ? error.message
            : "Could not load provider launch options.";
      setCatalogErrorsByID((current) => ({ ...current, [account.id]: message }));
    } finally {
      setCatalogLoadingByID((current) => ({ ...current, [account.id]: false }));
    }
  }

  async function uploadWorkloadArtifact(
    file: File,
    kind: "data" | "weights",
  ): Promise<WorkloadArtifactUpload> {
    setArtifactStage(`Preparing ${file.name}`);
    const session = await apiRequest<{
      upload_url?: string;
      artifact_ref?: string;
      chunk_size_bytes?: number;
      max_size_bytes?: number;
    }>("/api/v1/weight-uploads", {
      method: "POST",
      body: {
        artifact_kind: kind,
        expires_in_s: 86_400,
      },
      timeoutMs: 20_000,
      cache: "no-store",
    });
    const uploadURL = String(session.upload_url || "").trim();
    const artifactRef = String(session.artifact_ref || "").trim();
    if (!uploadURL || !artifactRef) {
      throw new APIError(
        500,
        "ERR_UPLOAD_SESSION_INVALID",
        "GENRL could not create a signed upload session.",
      );
    }
    const maxSizeBytes = Number(session.max_size_bytes || 0);
    if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
      throw new APIError(
        413,
        "ERR_UPLOAD_TOO_LARGE",
        `${file.name} is ${formatBytes(file.size)}. This deployment accepts browser uploads up to ${formatBytes(maxSizeBytes)}; use the external data/model path for larger datasets.`,
      );
    }
    const chunkSize = normalizeUploadChunkSize(session.chunk_size_bytes);
    if (file.size === 0) {
      setArtifactStage(`Uploading ${file.name}`);
      await apiRequest(uploadURL, {
        method: "PUT",
        rawBody: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
        timeoutMs: workloadUploadTimeoutMs(file),
        cache: "no-store",
      });
    } else {
      let offset = 0;
      while (offset < file.size) {
        const endExclusive = Math.min(offset + chunkSize, file.size);
        const chunk = file.slice(offset, endExclusive);
        const checksum = await sha256Hex(chunk);
        const percent = Math.max(1, Math.floor((endExclusive / file.size) * 100));
        setArtifactStage(`Uploading ${file.name} · ${percent}%`);
        const status = await apiRequest<WorkloadUploadStatus>(uploadURL, {
          method: "PUT",
          rawBody: chunk,
          headers: {
            "Content-Type": file.type || "application/octet-stream",
            "Content-Range": `bytes ${offset}-${endExclusive - 1}/${file.size}`,
            "X-Chunk-SHA256": checksum,
          },
          timeoutMs: workloadUploadChunkTimeoutMs(chunk),
          cache: "no-store",
        });
        const received = Number(status.received_bytes || 0);
        offset = received > offset ? received : endExclusive;
      }
    }
    return {
      artifact_ref: artifactRef,
      filename: file.name,
      size_bytes: file.size,
      content_type: file.type || "application/octet-stream",
      kind,
      source: "browser_upload",
    };
  }

  async function stageLaunchArtifacts(): Promise<{
    inputArtifacts: WorkloadArtifactUpload[];
    modelWeights?: WorkloadArtifactUpload;
  }> {
    const inputArtifacts: WorkloadArtifactUpload[] = [];
    if (String(form.input_data_uri || "").trim()) {
      inputArtifacts.push({
        source_uri: String(form.input_data_uri || "").trim(),
        filename: "external-data",
        kind: "data",
        source: "external_uri",
      });
    }
    for (const file of dataFiles) {
      inputArtifacts.push(await uploadWorkloadArtifact(file, "data"));
    }
    const modelWeights = form.use_custom_model && modelWeightsFile
      ? await uploadWorkloadArtifact(modelWeightsFile, "weights")
      : undefined;
    setArtifactStage("");
    return { inputArtifacts, modelWeights };
  }

  async function handleLaunch() {
    if (!selectedAccount && managedLaunchCapabilityBlocker) {
      toast.error(managedLaunchCapabilityBlocker);
      return;
    }
    const serviceName = String(form.name || "").trim();
    if (!serviceName) {
      toast.error("Name this service before provisioning it.");
      return;
    }
    if (!directModelFreeLaunch && !String(form.prompt || "").trim()) {
      toast.error("Tell GENRL what you want to run first.");
      return;
    }
    if (selectedAccountDescriptor?.launch_inputs.length) {
      const missing = providerMissingRequiredInputs(
        selectedAccountDescriptor.launch_inputs,
        providerInputValues,
      );
      if (missing.length > 0) {
        toast.error(`Complete the required provider fields: ${missing.join(", ")}.`);
        return;
      }
    }
    setLaunching(true);
    setArtifactStage("");
    const launchToastID = toast.loading(
      selectedAccount
        ? `Launching ${selectedAccount.provider_name} compute…`
        : "Routing across GENRL managed capacity…",
    );
    let clientLaunchID = "";
    try {
      const customModelRef = String(form.custom_model_ref || "").trim();
      if (!directModelFreeLaunch && form.use_custom_model && !customModelRef && !modelWeightsFile) {
        toast.dismiss(launchToastID);
        toast.error("Add a Hugging Face model, storage URL, or weights file first.");
        setLaunching(false);
        return;
      }
      const staged = directModelFreeLaunch
        ? { inputArtifacts: [] as WorkloadArtifactUpload[], modelWeights: undefined }
        : await stageLaunchArtifacts();
      const action = selectedAccount ? "provider_job_launch" : "abstracted_provider_job_launch";
      const quoteID = `quote_ui_${Date.now()}`;
      clientLaunchID = createClientLaunchID();
      const launchIntent = {
        launch_intent: PROVIDER_LAUNCH_INTENT,
        client_launch_id: clientLaunchID,
      };
      const routeEstimate = estimatedMinutes(form.workload_type, form.routing_preference);
      const runtimeModel = directModelFreeLaunch
        ? ""
        : form.use_custom_model
          ? customModelRef || staged.modelWeights?.artifact_ref || ""
          : form.model_id === "genrl-recommended"
            ? ""
            : form.model_id;
      const inputArtifactRefs = staged.inputArtifacts
        .map((artifact) => artifact.artifact_ref || artifact.source_uri || "")
        .filter(Boolean);
      const artifactPayload = {
        input_artifacts: staged.inputArtifacts,
        input_artifact_refs: inputArtifactRefs,
        input_data_uri: String(form.input_data_uri || "").trim(),
        model_weights_ref: form.use_custom_model
          ? staged.modelWeights?.artifact_ref || customModelRef || undefined
          : undefined,
        runtime_model_ref: runtimeModel || undefined,
      };
      const snapshotPayload = runSnapshotLaunchPayload(form.save_run_context, form.session_mode);
      const directProviderInputs = selectedAccountDescriptor
        ? providerLaunchInputPayload(
            selectedAccountDescriptor.launch_inputs,
            providerInputValues,
          )
        : {};
      const legacyDirectVMInputs =
        String(selectedAccount?.provider_slug || "").trim().toLowerCase() === "hyperstack"
          ? {
              environment_name: form.environment_name || undefined,
              flavor_name: form.flavor_name || undefined,
              image_name: form.image_name || undefined,
              key_name: form.key_name || undefined,
              assign_public_ip: form.assign_public_ip,
            }
          : {};
      const directRawComputePayload = providerDirectRawComputeLaunchPayload({
        accountID: form.account_id,
        name: serviceName,
        providerInputs: directProviderInputs,
        extraInputs: legacyDirectVMInputs,
        launchIntent,
      });
      let payload: Record<string, unknown> = selectedAccount
        ? directModelFreeLaunch
          ? {
              ...directRawComputePayload,
              service_name: serviceName,
            }
          : {
              ...form,
              ...directProviderInputs,
              name: serviceName,
              service_name: serviceName,
              account_id: form.account_id,
              runtime_model: runtimeModel,
              model_id: form.use_custom_model ? "custom-model" : form.model_id,
              ...artifactPayload,
              ...snapshotPayload,
              ...launchIntent,
            }
        : {
            name: serviceName,
            service_name: serviceName,
            workload_type: form.workload_type,
            model_id: form.use_custom_model ? "custom-model" : form.model_id,
            prompt: form.prompt,
            routing_preference: form.routing_preference,
            max_price_credits: form.max_price_credits,
            estimated_minutes: routeEstimate,
            gpu_type_id: form.gpu_type_id,
            hardware_label: selectedHardwareOption?.label || form.gpu_type_id || undefined,
            gpu_count: form.gpu_count,
            account_id: "",
            provider_slug: form.provider_slug,
            runtime_model: runtimeModel,
            restore_context: form.restore_context || undefined,
            ...artifactPayload,
            ...snapshotPayload,
            wallet_quote_id: quoteID,
            wallet_reserve_amount_micros: reserveMicrosFromCredits(form.max_price_credits),
            wallet_reserve_idempotency_key: `${quoteID}:${Math.random().toString(36).slice(2, 10)}`,
            lease_ttl_hours: 1,
            require_attestation: true,
            wallet_route_type: "genrl_abstracted",
            ...launchIntent,
          };
      if (!directModelFreeLaunch) {
        setArtifactStage("Planning workload packs…");
        const planResponse = await apiRequest<WorkloadPlanResponse>("/api/v1/workloads/plan", {
          method: "POST",
          body: payload,
          timeoutMs: 20000,
        });
        payload = {
          ...payload,
          ...(planResponse.launch_request || {}),
          name: serviceName,
          service_name: serviceName,
          ...launchIntent,
        };
      }
      if (!directModelFreeLaunch && form.session_mode === "persistent") {
        const response = await apiRequest<RuntimeSessionPayload>("/api/v1/runtime/sessions", {
          method: "POST",
          body: {
            ...payload,
            ...launchIntent,
            session_mode: "persistent",
            persist_vm: true,
            max_spend_micros: reserveMicrosFromCredits(form.max_price_credits),
            idle_timeout_seconds: form.idle_timeout_seconds,
            max_ttl_seconds: form.max_ttl_seconds,
            auto_stop_idle: form.auto_stop_idle,
            wallet_route_type: selectedAccount ? "provider_direct" : "genrl_abstracted",
          },
          timeoutMs: PROVIDER_LAUNCH_TIMEOUT_MS,
        });
        upsertRuntimeSession(
          response.session,
          response.turns || (response.turn ? [response.turn] : undefined),
        );
        setLaunchOpen(false);
        setForm(launchDefaults());
        setDataFiles([]);
        setModelWeightsFile(null);
        toast.dismiss(launchToastID);
        toast.success("Multi-runtime VM is preparing the first output");
        void refresh({ silent: true });
        return;
      }
      const response = await apiRequest<{ job: ProviderRun }>(
        `/api/wallet/accounts?action=${action}`,
        {
          method: "POST",
          body: payload,
          timeoutMs: PROVIDER_LAUNCH_TIMEOUT_MS,
        },
      );
      upsertJob(response.job);
      setLaunchOpen(false);
      setForm(launchDefaults());
      setDataFiles([]);
      setModelWeightsFile(null);
      toast.dismiss(launchToastID);
      toast.success(
        selectedAccount
          ? `${selectedAccount.provider_name} launch started`
          : "GENRL compute launched; prompt will run automatically",
      );
      void refresh({ silent: true });
    } catch (error) {
      toast.dismiss(launchToastID);
      if (clientLaunchID && providerLaunchOutcomeMayBeUnknown(error)) {
        const recovered = await recoverProviderJobLaunch<ProviderRun>(clientLaunchID, async () => {
          const payload = await apiRequest<{ jobs?: ProviderRun[] }>(
            "/api/wallet/accounts?action=provider_jobs&fast=1",
            { method: "GET", timeoutMs: 12000, cache: "no-store" },
          );
          return Array.isArray(payload.jobs) ? payload.jobs : [];
        });
        if (recovered) {
          upsertJob(recovered);
          setLaunchOpen(false);
          setForm(launchDefaults());
          setDataFiles([]);
          setModelWeightsFile(null);
          if (workloadHasClosed(recovered)) {
            toast.error(
              userSafeLaunchError(
                recovered.last_error || "Provider launch ended before compute became ready.",
              ),
            );
          } else {
            toast.success(
              selectedAccount
                ? `${selectedAccount.provider_name} launch started`
                : "GENRL compute launched; prompt will run automatically",
            );
          }
          void refresh({ silent: true });
          return;
        }
      }
      if (error instanceof APIError && error.code === "ERR_TIMEOUT") {
        await refresh({ silent: true });
        toast.info(userSafeLaunchError(error));
        return;
      }
      toast.error(userSafeLaunchError(error));
    } finally {
      setArtifactStage("");
      setLaunching(false);
    }
  }

  async function handleTerminate(jobID: string) {
    try {
      const response = await apiRequest<{ job: ProviderRun }>(
        "/api/wallet/accounts?action=provider_jobs",
        {
          method: "DELETE",
          body: { id: jobID },
          timeoutMs: 32000,
        },
      );
      upsertJob(response.job);
      toast.success("Provider job terminated");
    } catch (error) {
      const message =
        error instanceof APIError
          ? error.detail || "Could not terminate provider job."
          : "Could not terminate provider job.";
      toast.error(message);
    }
  }

  async function handleRuntimeSessionTurn(session: RuntimeSession) {
    const input = String(sessionInputs[session.id] || "").trim();
    if (!input) {
      toast.error("Add an input before continuing this runtime.");
      return;
    }
    setSessionBusyByID((current) => ({ ...current, [session.id]: true }));
    try {
      const response = await apiRequest<RuntimeSessionPayload>(
        `/api/v1/runtime/sessions/${session.id}/turns`,
        {
          method: "POST",
          body: { input_text: input },
          timeoutMs: PROVIDER_LAUNCH_TIMEOUT_MS,
        },
      );
      upsertRuntimeSession(response.session, [
        ...(sessionTurnsByID[session.id] || []),
        ...(response.turn ? [response.turn] : []),
      ]);
      setSessionInputs((current) => ({ ...current, [session.id]: "" }));
      toast.success("Runtime returned a new output");
    } catch (error) {
      toast.error(
        error instanceof APIError
          ? error.detail || "Runtime input failed."
          : "Runtime input failed.",
      );
    } finally {
      setSessionBusyByID((current) => ({ ...current, [session.id]: false }));
    }
  }

  async function handleRuntimeSessionStop(session: RuntimeSession) {
    setSessionBusyByID((current) => ({ ...current, [session.id]: true }));
    try {
      const response = await apiRequest<{ session: RuntimeSession }>(
        `/api/v1/runtime/sessions/${session.id}/stop`,
        {
          method: "POST",
          body: {},
          timeoutMs: 90000,
        },
      );
      upsertRuntimeSession(response.session);
      toast.success("Runtime stopped and price settled");
      void refresh({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof APIError
          ? error.detail || "Could not stop runtime."
          : "Could not stop runtime.",
      );
    } finally {
      setSessionBusyByID((current) => ({ ...current, [session.id]: false }));
    }
  }

  function openLaunch(account?: LaunchAccount) {
    setForm(launchDefaults(account));
    setDataFiles([]);
    setModelWeightsFile(null);
    setArtifactStage("");
    const descriptor = providerDescriptorForSlug(providerDescriptors, account?.provider_slug || "");
    setProviderInputValues(
      descriptor
        ? syncRegistryLaunchInputs(
            descriptor,
            undefined,
            providerLaunchInputDefaults(descriptor.launch_inputs, account?.request_body),
          )
        : {},
    );
    setLaunchOpen(true);
  }

  useEffect(() => {
    if (!launchOpen || !selectedAccount || !selectedAccount.directLaunch) return;
    void loadCatalog(selectedAccount);
  }, [launchOpen, selectedAccount?.id]);

  useEffect(() => {
    const providerSlug = !selectedAccount ? String(form.provider_slug || "").trim() : "";
    if (!providerSlug) {
      setManagedProviderHardware({
        providerSlug: "",
        options: [],
        loading: false,
        error: "",
      });
      return;
    }
    let active = true;
    setManagedProviderHardware({
      providerSlug,
      options: [],
      loading: true,
      error: "",
    });
    readAvailableHardwareOptions(providerSlug)
      .then((options) => {
        if (!active) return;
        setManagedProviderHardware({
          providerSlug,
          options,
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (!active) return;
        setManagedProviderHardware({
          providerSlug,
          options: [],
          loading: false,
          error: error instanceof Error ? error.message : "Could not load provider hardware.",
        });
      });
    return () => {
      active = false;
    };
  }, [form.provider_slug, selectedAccount?.id]);

  useEffect(() => {
    if (!selectedAccount || !selectedCatalog) return;
    setForm((current) => {
      if (current.account_id !== selectedAccount.id) return current;
      return syncFormWithCatalog(current, selectedAccount, selectedCatalog);
    });
    if (selectedAccountDescriptor?.launch_inputs.length) {
      setProviderInputValues((current) =>
        syncRegistryLaunchInputs(
          selectedAccountDescriptor,
          selectedCatalog,
          {
            ...providerLaunchInputDefaults(
              selectedAccountDescriptor.launch_inputs,
              selectedAccount.request_body,
            ),
            ...current,
          },
        ),
      );
    }
  }, [selectedAccount?.id, selectedAccountDescriptor, selectedCatalog]);

  useEffect(() => {
    if (!detailOpen || !selectedJobID) return;
    void handleProbeJob(selectedJobID, { silent: false });
  }, [detailOpen, selectedJobID]);

  const hyperstackCatalog = isHyperstackCatalog(selectedCatalog) ? selectedCatalog : undefined;
  const runPodCatalog = isRunPodCatalog(selectedCatalog) ? selectedCatalog : undefined;
  const hyperstackRegion = hyperstackCatalog
    ? hyperstackEnvironmentRegion(hyperstackCatalog.environments, form.environment_name)
    : "";
  const hyperstackFlavorOptions = hyperstackCatalog
    ? hyperstackCatalog.flavors.filter(
        (row) => !hyperstackRegion || !row.region || row.region.toUpperCase() === hyperstackRegion,
      )
    : [];
  const hyperstackInStockFlavorOptions = hyperstackFlavorOptions.filter((row) =>
    hyperstackFlavorHasStock(row),
  );
  const hyperstackSelectableFlavorOptions =
    hyperstackInStockFlavorOptions.length > 0
      ? hyperstackInStockFlavorOptions
      : hyperstackFlavorOptions;
  const hyperstackImageOptions = hyperstackCatalog
    ? hyperstackCatalog.images.filter(
        (row) => !hyperstackRegion || !row.region || row.region.toUpperCase() === hyperstackRegion,
      )
    : [];
  const hyperstackKeypairOptions = hyperstackCatalog
    ? hyperstackKeypairsForEnvironment(hyperstackCatalog.keypairs, form.environment_name)
    : [];
  const selectedHyperstackFlavor =
    hyperstackSelectableFlavorOptions.find((row) => row.name === form.flavor_name) ||
    hyperstackFlavorOptions.find((row) => row.name === form.flavor_name);
  const selectedRunPodGPU = runPodCatalog?.gpu_types.find((row) => row.id === form.gpu_type_id);
  const runPodCloudOptions = runPodCloudTypeOptions(selectedRunPodGPU);
  const runPodGpuCountOptions = runPodAvailableGpuCounts(selectedRunPodGPU, form.cloud_type);
  const hyperstackLaunchableFlavorCostBands = assignHardwareCostBands(
    hyperstackInStockFlavorOptions,
    (flavor) => flavor.price_per_hour,
  );
  const hyperstackLaunchableFlavorCostBandByName = new Map(
    hyperstackInStockFlavorOptions.map((flavor, index) => [
      flavor.name,
      hyperstackLaunchableFlavorCostBands[index] || "Mid cost",
    ] as const),
  );
  const runPodLaunchableGPUOptions = (runPodCatalog?.gpu_types || []).filter(
    runPodGPUTypeLaunchable,
  );
  const runPodLaunchableGPUCostBands = assignHardwareCostBands(
    runPodLaunchableGPUOptions,
    runPodGPUHourlyCost,
  );
  const runPodLaunchableGPUCostBandByID = new Map(
    runPodLaunchableGPUOptions.map((gpu, index) => [
      gpu.id,
      runPodLaunchableGPUCostBands[index] || "Mid cost",
    ] as const),
  );
  const runPodLaunchableCloudOptions = runPodCloudOptions.filter((cloudType) =>
    runPodCatalogPriceLaunchable(runPodPriceForCloud(selectedRunPodGPU, cloudType)),
  );
  const runPodLaunchableCloudCostBands = assignHardwareCostBands(
    runPodLaunchableCloudOptions,
    (cloudType) => runPodPriceForCloud(selectedRunPodGPU, cloudType)?.uninterruptable_price,
  );
  const runPodLaunchableCloudCostBandByType = new Map(
    runPodLaunchableCloudOptions.map((cloudType, index) => [
      cloudType,
      runPodLaunchableCloudCostBands[index] || "Mid cost",
    ] as const),
  );
  const selectedProvisioningMode = String(
    providerInputValues.provisioning_mode || "on_demand",
  );
  const registryLaunchFields = (selectedAccountDescriptor?.launch_inputs || []).filter((field) => {
    if (field.key === "idempotency_key") return false;
    if (field.key === "capacity_reservation_id") return selectedProvisioningMode === "reservation";
    if (field.key === "queue_timeout_seconds" || field.key === "max_run_duration_seconds") {
      return selectedProvisioningMode === "flex_start";
    }
    return true;
  });
  const missingRegistryLaunchFields = selectedAccountDescriptor
    ? providerMissingRequiredInputs(selectedAccountDescriptor.launch_inputs, providerInputValues)
    : [];
  const hyperstackLaunchBlockedReason =
    selectedAccount?.provider_slug === "hyperstack"
      ? selectedCatalogLoading
        ? "Loading Hyperstack launch options…"
        : !hyperstackCatalog
          ? "Load Hyperstack launch options first."
          : hyperstackFlavorOptions.length === 0
            ? `No flavors were returned for ${form.environment_name || "this environment"}.`
            : hyperstackInStockFlavorOptions.length === 0
              ? `No in-stock Hyperstack flavors are available in ${form.environment_name || "this environment"} right now.`
              : !selectedHyperstackFlavor || !hyperstackFlavorHasStock(selectedHyperstackFlavor)
                ? "Choose an in-stock Hyperstack flavor."
                : hyperstackImageOptions.length === 0
                  ? `No images were returned for ${form.environment_name || "this environment"}.`
                  : !form.image_name
                    ? "Choose a Hyperstack image."
                    : hyperstackKeypairOptions.length === 0
                      ? `No Hyperstack keypairs are available for ${form.environment_name || "this environment"}.`
                      : !form.key_name
                        ? "Choose a Hyperstack keypair."
                        : ""
      : "";
  const registryLaunchBlockedReason =
    registryLaunchFields.length === 0
      ? ""
      : selectedCatalogLoading && !selectedCatalog
        ? "Loading live provider launch options…"
        : selectedCatalogError && !selectedCatalog
          ? selectedCatalogError
          : missingRegistryLaunchFields.length > 0
            ? `Complete: ${missingRegistryLaunchFields.join(", ")}.`
            : "";
  const selectedAccountReadinessBlocker =
    selectedAccount?.launchReason && selectedAccount.launchReason !== "Ready"
      ? selectedAccount.launchReason
      : "";
  const launchBlockedReason = selectedAccountReadinessBlocker ||
    (selectedAccount?.provider_slug === "hyperstack"
      ? hyperstackLaunchBlockedReason
      : registryLaunchBlockedReason);
  const selectedJobKeypair = String(selectedJob?.launch_request?.key_name || "").trim();
  const selectedJobRuntime = String(jobProbe?.runtime_url || selectedJob?.runtime_url || "").trim();
  const selectedJobDocsURL = String(jobProbe?.docs_url || "").trim();
  const selectedJobConsoleURL = String(consoleResult?.url || "").trim();
  const inferenceReady =
    jobProbe?.mode === "openai-compatible" && jobProbe.reachable && !!selectedJobRuntime;
  const selectedJobSupportsConsole = canOpenProviderConsole(selectedJob);
  const selectedJobCanStart = canStartProviderJob(selectedJob, selectedJobDescriptor);
  const selectedJobCanStop = canStopProviderJob(selectedJob, selectedJobDescriptor);
  const selectedJobCanHibernate = canHibernateProviderJob(selectedJob, selectedJobDescriptor);
  const selectedJobCanRestore = canRestoreProviderJob(selectedJob, selectedJobDescriptor);
  const selectedJobCanReboot = canRebootProviderJob(selectedJob, selectedJobDescriptor);
  const selectedJobRestartAction = providerRestartAction(selectedJobDescriptor);
  const selectedJobManaged = isManagedWalletJob(selectedJob);
  const abstractedRouteEstimate = estimatedMinutes(form.workload_type, form.routing_preference);
  const abstractedReserveMicros = reserveMicrosFromCredits(form.max_price_credits);
  const abstractedRoutingLabel = optionLabel(ABSTRACTED_ROUTING_OPTIONS, form.routing_preference);
  const providerScopedHardwareOptions = form.provider_slug
    ? walletScopedProviderHardwareOptions(
        managedProviderHardware.providerSlug,
        form.provider_slug,
        managedProviderHardware.options,
      )
    : hardwareOptions;
  const selectedHardwareOption = form.gpu_type_id
    ? providerScopedHardwareOptions.find(
        (option) =>
          option.value === form.gpu_type_id ||
          normalizeHardwareKey(option.value) === normalizeHardwareKey(form.gpu_type_id),
      )
    : null;
  useEffect(() => {
    if (
      selectedAccount ||
      !form.provider_slug ||
      !form.gpu_type_id ||
      selectedHardwareOption
    ) {
      return;
    }
    setForm((current) => {
      if (
        current.provider_slug !== form.provider_slug ||
        current.gpu_type_id !== form.gpu_type_id
      ) {
        return current;
      }
      return { ...current, gpu_type_id: "", gpu_count: 1 };
    });
  }, [form.gpu_type_id, form.provider_slug, selectedAccount, selectedHardwareOption]);
  const fallbackHardwareLabel = optionLabel(ABSTRACTED_HARDWARE_OPTIONS, form.gpu_type_id);
  const hardwareSelectOptions = [
    ...(form.gpu_type_id && !selectedHardwareOption && !form.provider_slug
      ? [
          {
            value: form.gpu_type_id,
            label: fallbackHardwareLabel,
            detail: "Selected hardware preference",
            gpuCount: Math.max(1, Math.round(Number(form.gpu_count || 1))),
            stockAvailable: 0,
            providerCount: 0,
            providers: [],
            bestPricePerHour: undefined,
            originalPricePerHour: undefined,
            discountPercent: 0,
            fromProAccount: false,
            source: "fallback" as const,
          },
        ]
      : []),
    ...providerScopedHardwareOptions,
  ];
  const hardwareCostBands = assignHardwareCostBands(
    hardwareSelectOptions,
    (option) => option.bestPricePerHour,
  );
  const hardwareSelectRows = [
    { value: "", label: "Auto hardware" },
    ...hardwareSelectOptions.map((option, index) => ({
      value: option.value,
      label: `${option.label} ${hardwareCostBandLabel(hardwareCostBands[index] || "Mid cost")} · ${option.detail}`,
    })),
  ];
  const selectedHardwareCostBand = form.gpu_type_id
    ? hardwareCostBands[hardwareSelectOptions.findIndex(
        (option) => option.value === form.gpu_type_id ||
          normalizeHardwareKey(option.value) === normalizeHardwareKey(form.gpu_type_id),
      )]
    : undefined;
  const abstractedHardwareLabel = selectedHardwareOption?.label
    ? `${selectedHardwareOption.label} ${hardwareCostBandLabel(selectedHardwareCostBand || "Mid cost")}`
    : fallbackHardwareLabel || "Auto hardware";
  const abstractedProviderRequirements = {
    customModels: form.use_custom_model,
    inputArtifacts: dataFiles.length > 0 || Boolean(String(form.input_data_uri || "").trim()),
    workloadType: form.workload_type,
    sessionMode: form.session_mode,
    runSnapshots: form.save_run_context,
    snapshotRestore: Boolean(form.restore_context),
  };
  const compatibleManagedProviderOptions = providerManagedWorkloadOptions(
    providerDescriptors,
    abstractedProviderRequirements,
  );
  const selectedManagedProviderCompatible =
    !form.provider_slug ||
    compatibleManagedProviderOptions.some((option) => option.value === form.provider_slug);
  const selectedIncompatibleProvider =
    form.provider_slug && !selectedManagedProviderCompatible
      ? providerDescriptorForSlug(providerDescriptors, form.provider_slug)
      : undefined;
  const abstractedProviderOptions: Array<{
    value: string;
    label: string;
    disabled?: boolean;
  }> =
    providerDescriptors.length > 0
      ? [
          { value: "", label: "Auto" },
          ...(selectedIncompatibleProvider
            ? [
                {
                  value: form.provider_slug,
                  label: `${selectedIncompatibleProvider.display_name || form.provider_slug} — incompatible`,
                  disabled: true,
                },
              ]
            : []),
          ...compatibleManagedProviderOptions,
        ]
      : [...LEGACY_ABSTRACTED_PROVIDER_OPTIONS];
  const managedProviderCapabilityBlocker =
    providerDescriptors.length === 0
      ? ""
      : !selectedManagedProviderCompatible
        ? `${selectedIncompatibleProvider?.display_name || form.provider_slug} cannot run the selected workload settings. Choose a compatible provider.`
        : compatibleManagedProviderOptions.length === 0
          ? "No enabled provider supports the selected workload settings."
          : "";
  const managedHardwareCapabilityBlocker =
    !selectedAccount && form.provider_slug && form.gpu_type_id && !selectedHardwareOption
      ? "The selected hardware is not advertised by this provider. Choose compatible hardware."
      : "";
  const managedLaunchCapabilityBlocker =
    managedProviderCapabilityBlocker || managedHardwareCapabilityBlocker;
  const abstractedProviderLabel = optionLabel(abstractedProviderOptions, form.provider_slug);
  const abstractedModelLabel = optionLabel(ABSTRACTED_MODEL_OPTIONS, form.model_id);
  const activeRuntimeSessions = runtimeSessions.filter(runtimeSessionIsActive);
  const activeRunCount = visibleSortedJobs.filter(workloadIsActive).length;
  const completedRunCount = visibleSortedJobs.filter((job) => !!managedRuntimeResult(job)).length;
  const totalSpentCredits = visibleSortedJobs.reduce(
    (sum, job) => sum + workloadCostCredits(job),
    0,
  );
  const dataFileBytes = dataFiles.reduce((sum, file) => sum + file.size, 0);
  const workloadPlanPreview = useMemo(
    () => inferWorkloadPlanPreview(form, dataFiles),
    [
      form.input_data_uri,
      form.pack_mode,
      form.prompt,
      form.selected_packs,
      form.workload_type,
      dataFiles,
    ],
  );
  const customModelReady =
    !form.use_custom_model || !!modelWeightsFile || !!String(form.custom_model_ref || "").trim();
  const runButtonDisabled =
    launching ||
    !String(form.name || "").trim() ||
    !String(form.prompt || "").trim() ||
    !customModelReady ||
    Boolean(managedLaunchCapabilityBlocker);
  return (
    <>
      <div className="compute-wallet-page compute-workloads-wallet-page compute-workloads-redesign">
        <div className="compute-wallet-shell compute-workloads-wallet-shell">
          <main className="compute-wallet-main compute-workloads-wallet-main">
            <header className="compute-wallet-header compute-workloads-wallet-header">
              <div>
                <h1>RUN WORKLOAD</h1>
                <p>Run a model, file task, or custom-weight workload through GENRL.</p>
              </div>
              <div className="compute-wallet-header-actions">
                {directLaunchAccounts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => openLaunch(directLaunchAccounts[0])}
                    className="compute-wallet-circle-button"
                    aria-label="Launch provider compute"
                    title="Launch provider compute"
                  >
                    <Server className="h-[18px] w-[18px]" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void refresh({ silent: true })}
                  disabled={refreshing}
                  className="compute-wallet-circle-button"
                  aria-label="Refresh workload history"
                  title="Refresh workload history"
                >
                  <RefreshCcw className={cn("h-[18px] w-[18px]", refreshing && "animate-spin")} />
                </button>
                <div>
                  <strong>
                    {refreshing
                      ? "Refreshing"
                      : cacheUpdatedAt
                        ? `Synced ${relTime(cacheUpdatedAt)}`
                        : "Ready"}
                  </strong>
                  <small>Workloads</small>
                </div>
              </div>
            </header>

            <section
              className="compute-wallet-hero compute-workloads-wallet-hero"
              aria-label="Run workload"
            >
              <div className="compute-workloads-redesign-composer compute-workloads-redesign-run-card compute-workloads-wallet-composer">
                <section
                  className="compute-workloads-redesign-section compute-workloads-redesign-task-box"
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "copy";
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (event.dataTransfer.files?.length) {
                      setDataFiles((current) =>
                        appendUniqueFiles(current, event.dataTransfer.files),
                      );
                    }
                  }}
                >
                  <div className="compute-workloads-redesign-section-label">
                    <span>Service name</span>
                    <p>Required</p>
                  </div>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, name: event.target.value }))
                    }
                    maxLength={64}
                    required
                    aria-required="true"
                    placeholder="e.g. research-vm"
                    className="compute-workloads-redesign-inline-input"
                  />
                  <div className="compute-workloads-redesign-section-label">
                    <span>Request</span>
                    <p>Required</p>
                  </div>
                  <Textarea
                    value={form.prompt}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, prompt: event.target.value }))
                    }
                    placeholder="Write the task, prompt, or instruction."
                    className="compute-workloads-redesign-textarea"
                  />

                  <div className="compute-workloads-redesign-prompt-tools">
                    <label className="compute-workloads-redesign-inline-upload">
                      <input
                        type="file"
                        multiple
                        onChange={(event) => {
                          if (event.target.files?.length) {
                            setDataFiles((current) =>
                              appendUniqueFiles(current, event.target.files!),
                            );
                          }
                          event.currentTarget.value = "";
                        }}
                      />
                      <Upload className="h-4 w-4" />
                      <span>
                        {dataFiles.length > 0
                          ? `${dataFiles.length} file${dataFiles.length === 1 ? "" : "s"} selected`
                          : "Add files"}
                      </span>
                      <small>
                        {dataFiles.length > 0
                          ? `${formatBytes(dataFileBytes)} ready`
                          : "Drop files anywhere in this box"}
                      </small>
                    </label>

                    <input
                      type="text"
                      value={form.input_data_uri}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, input_data_uri: event.target.value }))
                      }
                      placeholder="Data link"
                      className="compute-workloads-redesign-inline-input compute-workloads-redesign-data-link"
                    />
                  </div>

                  {dataFiles.length > 0 && (
                    <div className="compute-workloads-redesign-file-list">
                      {dataFiles.slice(0, 4).map((file) => (
                        <span key={fileKey(file)}>
                          <FileText className="h-3.5 w-3.5" />
                          {file.name}
                          <em>{formatBytes(file.size)}</em>
                          <button
                            type="button"
                            onClick={() =>
                              setDataFiles((current) =>
                                current.filter((candidate) => fileKey(candidate) !== fileKey(file)),
                              )
                            }
                            aria-label={`Remove ${file.name}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      {dataFiles.length > 4 && <span>+{dataFiles.length - 4} more</span>}
                    </div>
                  )}

                  <div className="compute-workloads-redesign-plan-preview">
                    <div>
                      <Brain className="h-4 w-4" />
                      <span>
                        {workloadPlanPreview.effective_workload_type.replaceAll("_", " ")}
                      </span>
                      <strong>
                        {workloadPlanPreview.runtime_agent_required
                          ? "Runtime agent"
                          : "Model runtime"}
                      </strong>
                    </div>
                    <p>
                      Packs:{" "}
                      {workloadPlanPreview.required_packs
                        .map((pack) => pack.replaceAll("_", " "))
                        .join(", ")}
                    </p>
                  </div>
                </section>

                <details className="compute-workloads-redesign-section compute-workloads-redesign-compact-panel compute-workloads-redesign-model-box">
                  <summary>
                    <span>Model</span>
                    <strong>
                      {form.use_custom_model ? "Custom weights" : abstractedModelLabel}
                    </strong>
                    <em>Choose</em>
                  </summary>
                  <div
                    className="compute-workloads-redesign-model-mode"
                    role="group"
                    aria-label="Model source"
                  >
                    <button
                      type="button"
                      className={cn(
                        !form.use_custom_model && "compute-workloads-redesign-chip-active",
                      )}
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          use_custom_model: false,
                        }))
                      }
                    >
                      GENRL model
                    </button>
                    <button
                      type="button"
                      className={cn(
                        form.use_custom_model && "compute-workloads-redesign-chip-active",
                      )}
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          use_custom_model: true,
                        }))
                      }
                    >
                      Upload weights
                    </button>
                  </div>

                  {!form.use_custom_model ? (
                    <label className="compute-workloads-redesign-select-row">
                      <span>Model</span>
                      <select
                        value={form.model_id}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            model_id: event.target.value,
                          }))
                        }
                      >
                        {ABSTRACTED_MODEL_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <div className="compute-workloads-redesign-model-upload">
                      <label
                        className="compute-workloads-redesign-dropzone compute-workloads-redesign-model-dropzone"
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "copy";
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const file = event.dataTransfer.files?.[0];
                          if (file) setModelWeightsFile(file);
                        }}
                      >
                        <input
                          type="file"
                          onChange={(event) => {
                            setModelWeightsFile(event.target.files?.[0] || null);
                            event.currentTarget.value = "";
                          }}
                        />
                        <Upload className="h-4 w-4" />
                        <strong>
                          {modelWeightsFile ? modelWeightsFile.name : "Add model weights"}
                        </strong>
                        <small>
                          {modelWeightsFile
                            ? `${formatBytes(modelWeightsFile.size)} selected`
                            : "Drop a checkpoint or archive."}
                        </small>
                      </label>
                      {modelWeightsFile && (
                        <div className="compute-workloads-redesign-file-list">
                          <span>
                            <Boxes className="h-3.5 w-3.5" />
                            {modelWeightsFile.name}
                            <em>{formatBytes(modelWeightsFile.size)}</em>
                            <button
                              type="button"
                              onClick={() => setModelWeightsFile(null)}
                              aria-label={`Remove ${modelWeightsFile.name}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        </div>
                      )}
                      <input
                        type="text"
                        value={form.custom_model_ref}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            custom_model_ref: event.target.value,
                          }))
                        }
                        placeholder="Paste a Hugging Face model or model URL"
                        className="compute-workloads-redesign-inline-input"
                      />
                    </div>
                  )}
                </details>

                <details className="compute-workloads-redesign-section compute-workloads-redesign-compact-panel compute-workloads-redesign-settings-box">
                  <summary>
                    <span>Settings</span>
                    <strong>
                      {form.session_mode === "persistent" ? "Multi-runtime" : "One run VM"} ·{" "}
                      {abstractedRoutingLabel} · {runSnapshotSummaryLabel(form.save_run_context)} ·{" "}
                      {form.gpu_type_id ? abstractedHardwareLabel : "Auto hardware"} ·{" "}
                      {form.max_price_credits} credits max
                    </strong>
                    <em>Optional</em>
                  </summary>
                  <div className="compute-workloads-redesign-controls">
                    <div className="compute-workloads-redesign-routing">
                      <span>Runtime</span>
                      <div>
                        {[
                          { value: "one_shot", label: "One run VM" },
                          { value: "persistent", label: "Multi-runtime" },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={cn(
                              form.session_mode === option.value &&
                                "compute-workloads-redesign-chip-active",
                            )}
                            onClick={() =>
                              setForm((current) => ({
                                ...current,
                                session_mode: option.value as LaunchFormState["session_mode"],
                              }))
                            }
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="compute-workloads-redesign-routing">
                      <span>Priority</span>
                      <div>
                        {ABSTRACTED_ROUTING_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={cn(
                              form.routing_preference === option.value &&
                                "compute-workloads-redesign-chip-active",
                            )}
                            onClick={() =>
                              setForm((current) => ({
                                ...current,
                                routing_preference: option.value,
                              }))
                            }
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <label>
                      <span>Max spend</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={form.max_price_credits}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            max_price_credits: Number(event.target.value || 0),
                          }))
                        }
                      />
                    </label>
                  </div>

                  <div className="compute-workloads-redesign-advanced">
                    <div className="compute-workloads-redesign-advanced-header">
                      <span>Advanced</span>
                      <small>
                        {hardwareSelectOptions.some((option) => option.source !== "fallback")
                          ? `${hardwareSelectOptions.filter((option) => option.source !== "fallback").length} live GPU types`
                          : "No live GPU types"}
                      </small>
                    </div>
                    <label className="compute-workloads-redesign-select-row compute-workloads-redesign-hardware-select">
                      <span>Provider</span>
                      <select
                        value={form.provider_slug}
                        onChange={(event) => chooseManagedProvider(event.target.value)}
                      >
                        {abstractedProviderOptions.map((option) => (
                          <option
                            key={option.value || "auto"}
                            value={option.value}
                            disabled={option.disabled}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {managedLaunchCapabilityBlocker && (
                      <p className="compute-workloads-redesign-hardware-note">
                        {managedLaunchCapabilityBlocker}
                      </p>
                    )}
                    {form.provider_slug && managedProviderHardware.loading && (
                      <p className="compute-workloads-redesign-hardware-note">
                        Loading provider-specific cost band and stock…
                      </p>
                    )}
                    {form.provider_slug && managedProviderHardware.error && (
                      <p className="compute-workloads-redesign-hardware-note">
                        {managedProviderHardware.error}
                      </p>
                    )}
                    <label className="compute-workloads-redesign-select-row compute-workloads-redesign-hardware-select">
                      <span>Hardware</span>
                      <select
                        value={form.gpu_type_id}
                        onChange={(event) => chooseHardwarePreference(event.target.value)}
                      >
                        {hardwareSelectRows.map((option) => (
                          <option key={option.value || "auto"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedHardwareOption && (
                      <p className="compute-workloads-redesign-hardware-note">
                        {hardwareCostBandLabel(selectedHardwareCostBand || "Mid cost")} · {selectedHardwareOption.detail}
                        {selectedHardwareOption.providers.length > 0
                          ? ` · ${selectedHardwareOption.providers.slice(0, 3).join(", ")}`
                          : ""}
                      </p>
                    )}
                    <div className="compute-workloads-redesign-pack-panel">
                      <div className="compute-workloads-redesign-pack-header">
                        <span>Packs</span>
                        <div>
                          {[
                            { value: "auto", label: "Auto" },
                            { value: "manual", label: "Manual" },
                          ].map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={cn(
                                form.pack_mode === option.value &&
                                  "compute-workloads-redesign-chip-active",
                              )}
                              onClick={() =>
                                setForm((current) => ({
                                  ...current,
                                  pack_mode: option.value as LaunchFormState["pack_mode"],
                                }))
                              }
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {form.pack_mode === "manual" && (
                        <div className="compute-workloads-redesign-pack-grid">
                          {WORKLOAD_PACK_OPTIONS.map((pack) => {
                            const checked = form.selected_packs.includes(pack.value);
                            return (
                              <button
                                key={pack.value}
                                type="button"
                                className={cn(checked && "compute-workloads-redesign-chip-active")}
                                onClick={() =>
                                  setForm((current) => ({
                                    ...current,
                                    selected_packs: checked
                                      ? current.selected_packs.filter(
                                          (value) => value !== pack.value,
                                        )
                                      : [...current.selected_packs, pack.value],
                                  }))
                                }
                              >
                                {pack.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <p>
                        Required packs are added automatically. Packs only become billable when a
                        tool actually runs.
                      </p>
                    </div>
                    <div className="compute-workloads-redesign-context-toggle">
                      <div>
                        <span>Context</span>
                        <strong>{runSnapshotSummaryLabel(form.save_run_context)}</strong>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={form.save_run_context}
                        aria-label="Save workload outputs and context"
                        className={cn(
                          "compute-workloads-redesign-toggle",
                          form.save_run_context && "compute-workloads-redesign-toggle-on",
                        )}
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            save_run_context: !current.save_run_context,
                          }))
                        }
                      >
                        <span />
                      </button>
                    </div>
                    {form.session_mode === "persistent" && (
                      <div className="compute-workloads-redesign-controls">
                        <label>
                          <span>Idle stop</span>
                          <input
                            type="number"
                            min={5}
                            step={5}
                            value={Math.round(form.idle_timeout_seconds / 60)}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                idle_timeout_seconds: Math.max(
                                  60,
                                  Number(event.target.value || 0) * 60,
                                ),
                              }))
                            }
                          />
                        </label>
                        <label>
                          <span>Max runtime</span>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={Math.round(form.max_ttl_seconds / 3600)}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                max_ttl_seconds: Math.max(
                                  3600,
                                  Number(event.target.value || 0) * 3600,
                                ),
                              }))
                            }
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </details>

                <div className="compute-workloads-redesign-submit-row">
                  <p>
                    {artifactStage ||
                      managedLaunchCapabilityBlocker ||
                      (form.session_mode === "persistent"
                        ? "We reserve your max spend while the runtime stays open."
                        : "We reserve your max spend. You only pay for what runs.")}
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleLaunch()}
                    disabled={runButtonDisabled}
                    className="compute-workloads-redesign-run"
                  >
                    {launching ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Rocket className="h-4 w-4" />
                    )}
                    Run workload
                  </button>
                </div>
              </div>
            </section>
            <section
              className="compute-wallet-actions compute-workloads-wallet-actions"
              aria-label="Workload status"
            >
              <div className="compute-wallet-action-card">
                <span className="compute-wallet-action-icon compute-wallet-action-blue">
                  <Play className="h-5 w-5" />
                </span>
                <span>
                  <strong>{activeRunCount + activeRuntimeSessions.length}</strong>
                  <small>Running now</small>
                </span>
                <ChevronRight className="h-5 w-5" />
              </div>
              <div className="compute-wallet-action-card">
                <span className="compute-wallet-action-icon compute-wallet-action-green">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <span>
                  <strong>{completedRunCount}</strong>
                  <small>Completed runs</small>
                </span>
                <ChevronRight className="h-5 w-5" />
              </div>
              <div className="compute-wallet-action-card">
                <span className="compute-wallet-action-icon compute-wallet-action-amber">
                  <Wallet className="h-5 w-5" />
                </span>
                <span>
                  <strong>{formatCredits(totalSpentCredits)}</strong>
                  <small>Total spent</small>
                </span>
                <ChevronRight className="h-5 w-5" />
              </div>
              <button
                type="button"
                className="compute-wallet-action-card"
                onClick={() => void refresh({ silent: true })}
                disabled={refreshing}
              >
                <span className="compute-wallet-action-icon compute-wallet-action-teal">
                  <RefreshCcw className={cn("h-5 w-5", refreshing && "animate-spin")} />
                </span>
                <span>
                  <strong>{refreshing ? "Refreshing" : "Refresh"}</strong>
                  <small>Sync workload history</small>
                </span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </section>

            {accounts.length > 0 && (
              <section className="compute-wallet-card compute-wallet-activity-card">
                <header className="compute-wallet-card-header">
                  <div>
                    <Boxes className="h-5 w-5" />
                    <h2>Connected provider accounts</h2>
                  </div>
                  <span>
                    {accounts.filter(
                      (account) =>
                        account.directLaunch &&
                        (!account.launchReason || account.launchReason === "Ready"),
                    ).length} launchable
                  </span>
                </header>
                <div className="grid gap-3 p-4 md:grid-cols-2">
                  {accounts.map((account) => (
                    <article
                      key={account.id}
                      className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <strong className="block truncate text-sm text-[var(--wallet-ops-fg)]">
                            {account.display_name}
                          </strong>
                          <small className="mt-1 block text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
                            {account.provider_name || account.provider_slug} · {account.snapshot?.status || (account.active ? "connected" : "inactive")}
                          </small>
                        </div>
                        <button
                          type="button"
                          onClick={() => openLaunch(account)}
                          disabled={
                            !account.directLaunch ||
                            Boolean(account.launchReason && account.launchReason !== "Ready")
                          }
                          title={account.launchReason || undefined}
                          className="compute-workloads-simple-secondary-button disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Rocket className="h-3.5 w-3.5" />
                          Launch
                        </button>
                      </div>
                      {account.launchReason && account.launchReason !== "Ready" && (
                        <p className="mt-3 text-xs text-[var(--warning)]">
                          {account.launchReason}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            )}

            {activeRuntimeSessions.length > 0 && (
              <section className="compute-wallet-card compute-wallet-activity-card compute-workloads-runtime-sessions">
                <header className="compute-wallet-card-header">
                  <div>
                    <Brain className="h-5 w-5" />
                    <h2>Active runtimes</h2>
                  </div>
                  <span>{activeRuntimeSessions.length} open</span>
                </header>
                <div className="compute-workloads-runtime-session-list">
                  {activeRuntimeSessions.map((session) => {
                    const busy = !!sessionBusyByID[session.id] || session.state === "running_input";
                    const turns = sessionTurnsByID[session.id] || [];
                    const orderedTurns = [...turns].sort((a, b) => a.sequence - b.sequence);
                    const activeTurn = [...orderedTurns]
                      .reverse()
                      .find((turn) =>
                        ["queued", "running"].includes(String(turn.status || "").toLowerCase()),
                      );
                    const latestResultTurn = [...orderedTurns]
                      .reverse()
                      .find((turn) => Boolean(turn.output_text || turn.error_message));
                    const outputText =
                      latestResultTurn?.output_text ||
                      latestResultTurn?.error_message ||
                      session.last_output_preview ||
                      session.last_error ||
                      "";
                    const hasOutput = Boolean(outputText.trim());
                    const canSendNextInput =
                      hasOutput && !busy && runtimeSessionCanAcceptNextInput(session);
                    const outputLabel =
                      latestResultTurn?.error_message || session.last_error
                        ? "Latest error"
                        : "Latest output";
                    const contextLabel = runtimeSessionContextLabel(session);
                    const waitingMessage = activeTurn
                      ? `Waiting for output from: ${activeTurn.input_text || "current input"}`
                      : "Waiting for the first output before accepting another input.";
                    return (
                      <article key={session.id} className="compute-workloads-runtime-session-card">
                        <div className="compute-workloads-runtime-session-head">
                          <div>
                            <strong>{session.name || "Persistent runtime"}</strong>
                            <small>
                              {session.provider_name || session.provider_slug} ·{" "}
                              {String(
                                session.metadata?.hardware ||
                                  session.runtime_model ||
                                  "GPU runtime",
                              )}
                              {contextLabel !== "—" ? ` · ${contextLabel}` : ""}
                            </small>
                          </div>
                          <span className="compute-workloads-redesign-pill compute-workloads-redesign-pill-good">
                            {runtimeSessionStateLabel(session)}
                          </span>
                        </div>
                        <div className="compute-workloads-runtime-session-output-block">
                          <span>{hasOutput ? outputLabel : "Waiting for output"}</span>
                          {latestResultTurn?.input_text ? (
                            <small>Input: {latestResultTurn.input_text}</small>
                          ) : null}
                          <p>{hasOutput ? outputText : waitingMessage}</p>
                        </div>
                        {canSendNextInput ? (
                          <div className="compute-workloads-runtime-session-input">
                            <input
                              className="compute-workloads-redesign-inline-input"
                              value={sessionInputs[session.id] || ""}
                              onChange={(event) =>
                                setSessionInputs((current) => ({
                                  ...current,
                                  [session.id]: event.target.value,
                                }))
                              }
                              placeholder="Send another input"
                            />
                            <button
                              type="button"
                              className="compute-workloads-simple-secondary-button"
                              disabled={!String(sessionInputs[session.id] || "").trim()}
                              onClick={() => void handleRuntimeSessionTurn(session)}
                            >
                              <Play className="h-4 w-4" />
                              Send
                            </button>
                            <button
                              type="button"
                              className="compute-workloads-simple-secondary-button"
                              disabled={!!sessionBusyByID[session.id]}
                              onClick={() => void handleRuntimeSessionStop(session)}
                            >
                              <Power className="h-4 w-4" />
                              Stop
                            </button>
                          </div>
                        ) : (
                          <div className="compute-workloads-runtime-session-input">
                            <p className="compute-workloads-runtime-session-pending">
                              {busy
                                ? "Next input unlocks when this output is ready."
                                : "Output must appear before another input can be sent."}
                            </p>
                            <button
                              type="button"
                              className="compute-workloads-simple-secondary-button"
                              disabled={!!sessionBusyByID[session.id]}
                              onClick={() => void handleRuntimeSessionStop(session)}
                            >
                              <Power className="h-4 w-4" />
                              Stop
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="compute-wallet-card compute-wallet-activity-card compute-workloads-redesign-history compute-workloads-wallet-history">
              <header className="compute-wallet-card-header">
                <div>
                  <Activity className="h-5 w-5" />
                  <h2>Recent workloads</h2>
                </div>
                <div className="compute-workloads-redesign-history-stats">
                  <span>{activeRunCount + activeRuntimeSessions.length} running</span>
                  <span>{completedRunCount} completed</span>
                  <span>{formatCredits(totalSpentCredits)} spent</span>
                </div>
              </header>

              {loading && jobs.length === 0 ? (
                <EmptyState
                  icon={Loader2}
                  title="Loading workload history"
                  body="GENRL is loading your latest runs."
                />
              ) : visibleSortedJobs.length === 0 ? (
                <EmptyState
                  icon={Boxes}
                  title="No runs yet"
                  body="Your first result will appear here with the request, answer, cost, timing, and proof."
                />
              ) : (
                <div className="compute-workloads-redesign-list">
                  {visibleSortedJobs.map((job) => {
                    const canTerminate = [
                      "launching",
                      "queued",
                      "running",
                      "stopped",
                      "unknown",
                    ].includes(job.status);
                    const runtimeResult = managedRuntimeResult(job);
                    const runtimeState = managedRuntimeStatus(job);
                    const runtimeError = managedRuntimeError(job);
                    const verificationOnly = runtimeResultIsVerificationOnly(runtimeResult);
                    const terminalWithoutResult = !runtimeResult && workloadHasClosed(job);
                    const input = workloadInputLabel(job);
                    const output = workloadOutputLabel(
                      job,
                      runtimeResult,
                      runtimeState,
                      runtimeError,
                    );
                    const outputIssue = runtimeResultIssue(job, runtimeResult);
                    const proof = workloadProof(job, runtimeResult);
                    const launchFailedWithoutResult = !runtimeResult && !!job.last_error;
                    const costBandLabel = hardwareCostBandLabel(
                      visibleJobCostBandByID.get(job.id) || "Mid cost",
                    );
                    return (
                      <details key={job.id} className="compute-workloads-redesign-row">
                        <summary>
                          <span
                            className={cn(
                              "compute-workloads-redesign-status-dot",
                              runtimeResult &&
                                !outputIssue &&
                                "compute-workloads-redesign-status-dot-complete",
                              (terminalWithoutResult || outputIssue) &&
                                "compute-workloads-redesign-status-dot-failed",
                            )}
                          >
                            <ShieldCheck className="h-3.5 w-3.5" />
                          </span>
                          <div className="compute-workloads-redesign-row-input">
                            <strong>
                              {verificationOnly
                                ? "Verification run"
                                : runtimeResult
                                  ? input
                                  : runtimeError
                                    ? "Prompt did not complete"
                                    : launchFailedWithoutResult
                                      ? "Launch failed"
                                      : terminalWithoutResult
                                        ? "Run ended without output"
                                        : input}
                            </strong>
                            <small>{relTime(runtimeResult?.checked_at || job.updated_at)}</small>
                          </div>
                          <p className="compute-workloads-redesign-row-output">{output}</p>
                          <div className="compute-workloads-redesign-row-meta">
                            <span>{workloadDurationLabel(job, runtimeResult)}</span>
                            <span>{workloadHardwareLabel(job)}</span>
                            <span>{costBandLabel}</span>
                            <span>{workloadCostLabel(job)}</span>
                          </div>
                          <span
                            className={cn(
                              "compute-workloads-redesign-pill",
                              proof.tone === "verified" &&
                                !outputIssue &&
                                "compute-workloads-redesign-pill-good",
                            )}
                          >
                            {outputIssue ? "Needs rerun" : proof.label}
                          </span>
                          <ChevronRight className="compute-workloads-history-chevron h-3.5 w-3.5" />
                        </summary>

                        <div className="compute-workloads-redesign-details">
                          <div className="compute-workloads-run-facts">
                            <DataCell label="Hardware" value={workloadHardwareLabel(job)} />
                            <DataCell label="Provider" value={workloadProviderLocationLabel(job)} />
                            <DataCell label="Cost band" value={costBandLabel} />
                            <DataCell
                              label={workloadPriceFactLabel(job)}
                              value={workloadCostLabel(job)}
                            />
                            <DataCell
                              label="Verification"
                              value={outputIssue ? "Needs rerun" : proof.label}
                            />
                            <DataCell label="Context" value={workloadContextLabel(job)} />
                          </div>
                          <div className="compute-workloads-proof-note">
                            <Wallet className="h-4 w-4" />
                            <p>
                              {workloadBillingDetail(job)} Cost band {costBandLabel} is relative to
                              the hardware shown in this workload history.
                            </p>
                          </div>

                          <div className="compute-workloads-redesign-answer-grid">
                            <section>
                              <span>Input</span>
                              <p>{input}</p>
                            </section>
                            <section>
                              <div>
                                <span>Output</span>
                                {runtimeResult && !outputIssue && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      void copyRuntimeOutput(runtimeResult.output_text)
                                    }
                                  >
                                    <Copy className="h-3 w-3" />
                                    Copy
                                  </button>
                                )}
                              </div>
                              <pre>{output}</pre>
                            </section>
                          </div>

                          <div
                            className={cn(
                              "compute-workloads-proof-note",
                              outputIssue && "compute-workloads-proof-note-warning",
                            )}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            <p>{outputIssue || proof.detail}</p>
                          </div>

                          {runtimeResult && !outputIssue && runSnapshotID(runtimeResult) && (
                            <div className="compute-workloads-proof-note">
                              <FileText className="h-4 w-4" />
                              <p>Snapshot saved</p>
                              <div className="ml-auto flex flex-wrap gap-2">
                                <a
                                  href={runSnapshotFileURL(runtimeResult, "output")}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                                >
                                  Output <ExternalLink className="h-3 w-3" />
                                </a>
                                <a
                                  href={runSnapshotFileURL(runtimeResult, "manifest")}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                                >
                                  Manifest <ExternalLink className="h-3 w-3" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => void handleUseRunSnapshot(runSnapshotID(runtimeResult))}
                                  className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                                >
                                  Use snapshot <RotateCcw className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )}

                          <details className="compute-workloads-redesign-technical">
                            <summary>
                              <span>Technical proof and provider details</span>
                              <ChevronRight className="h-3.5 w-3.5" />
                            </summary>
                            <div className="compute-workloads-data-grid">
                              <DataCell label="Provider" value={job.provider_name || "—"} />
                              <DataCell label="Provider wallet" value={job.account_name || "—"} />
                              <DataCell
                                label="Wallet status"
                                value={workloadSettlementStatus(job) || "pending"}
                              />
                              <DataCell
                                label="Reservation"
                                value={String(
                                  job.launch_request?.wallet_reservation_id ||
                                    job.launch_request?.reservation_id ||
                                    "—",
                                )}
                              />
                              <DataCell
                                label="Quote"
                                value={String(job.launch_request?.wallet_quote_id || "—")}
                              />
                              <DataCell
                                label="Proof state"
                                value={String(
                                  job.launch_request?.verification_status || proof.label,
                                )}
                              />
                              <DataCell label="Remote ID" value={job.remote_id || "—"} />
                              <DataCell label="Provider status" value={providerStatusLabel(job)} />
                              <DataCell label="Region / env" value={job.region || "—"} />
                              <DataCell label="GPU / flavor" value={job.gpu || "—"} />
                              <DataCell label="Cost band" value={costBandLabel} />
                              <DataCell
                                label="Billable time"
                                value={workloadBillableTimeLabel(job)}
                              />
                              <DataCell
                                label="Price source"
                                value={String(
                                  job.launch_request?.wallet_price_source ||
                                    job.launch_request?.price_source ||
                                    "—",
                                )}
                              />
                              <DataCell label="Image" value={job.image || "—"} />
                              <DataCell
                                label="Model"
                                value={
                                  runtimeResult?.model ||
                                  String(
                                    job.launch_request?.runtime_model ||
                                      job.launch_request?.model_id ||
                                      "—",
                                  )
                                }
                              />
                              <DataCell
                                label="Runtime URL"
                                value={runtimeResult?.runtime_url || job.runtime_url || "—"}
                              />
                              <DataCell label="Public IP" value={job.public_ip || "—"} />
                            </div>
                          </details>

                          <div className="compute-workloads-job-actions">
                            <button
                              type="button"
                              onClick={() => openJobDetail(job)}
                              className="compute-workloads-simple-secondary-button"
                            >
                              Full details
                              <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                            {job.runtime_url && (
                              <a
                                href={job.runtime_url}
                                target="_blank"
                                rel="noreferrer"
                                className="compute-workloads-simple-secondary-button"
                              >
                                Open runtime <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            {canTerminate && (
                              <button
                                type="button"
                                onClick={() => void handleTerminate(job.id)}
                                className="compute-workloads-simple-secondary-button"
                              >
                                <Square className="h-3.5 w-3.5" />
                                Destroy
                              </button>
                            )}
                          </div>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}
            </section>
          </main>
        </div>
      </div>

      <Dialog
        open={launchOpen}
        onOpenChange={(open) => {
          setLaunchOpen(open);
          if (!open) {
            setForm(launchDefaults());
            setProviderInputValues({});
            setDataFiles([]);
            setModelWeightsFile(null);
            setArtifactStage("");
          }
        }}
      >
        <DialogContent className="!flex max-h-[calc(100svh-2rem)] max-w-2xl flex-col gap-0 overflow-hidden border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-0 text-[var(--wallet-ops-fg)] sm:rounded-none">
          <DialogHeader className="shrink-0 border-b border-[var(--wallet-ops-border)] px-5 py-4">
            <DialogTitle className="text-base font-semibold tracking-normal">
              {selectedAccount && directModelFreeLaunch ? "Provision provider access" : "Launch compute"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[var(--wallet-ops-muted)]">
              {selectedAccount && directModelFreeLaunch
                ? `Choose the exact ${selectedAccount.provider_name || selectedAccount.provider_slug} infrastructure and access mode. Model prompts and weights stay out of direct-provider provisioning.`
                : "Choose workload filters and a max spend. GENRL routes to compatible managed capacity and reserves GENRL credits."}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <InputRow
              label="Service name *"
              value={form.name}
              onChange={(value) => setForm((current) => ({ ...current, name: value }))}
              placeholder="e.g. research-vm"
              maxLength={64}
              required
            />
            {!selectedAccount && (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectRow
                    label="Workload"
                    value={form.workload_type}
                    onChange={(value) =>
                      setForm((current) => ({ ...current, workload_type: value }))
                    }
                    options={[...ABSTRACTED_WORKLOAD_OPTIONS]}
                  />
                  <SelectRow
                    label="Model"
                    value={form.model_id}
                    onChange={(value) => setForm((current) => ({ ...current, model_id: value }))}
                    options={[...ABSTRACTED_MODEL_OPTIONS]}
                  />
                  <SelectRow
                    label="Routing preference"
                    value={form.routing_preference}
                    onChange={(value) =>
                      setForm((current) => ({ ...current, routing_preference: value }))
                    }
                    options={ABSTRACTED_ROUTING_OPTIONS.map((option) => ({
                      value: option.value,
                      label: `${option.label} · ${option.hint}`,
                    }))}
                  />
                  <SelectRow
                    label="Provider preference"
                    value={form.provider_slug}
                    onChange={chooseManagedProvider}
                    options={abstractedProviderOptions}
                  />
                  <SelectRow
                    label="Hardware preference"
                    value={form.gpu_type_id}
                    onChange={chooseHardwarePreference}
                    options={hardwareSelectRows}
                  />
                  <NumberRow
                    label="Scale"
                    value={form.gpu_count}
                    onChange={(value) => setForm((current) => ({ ...current, gpu_count: value }))}
                    min={1}
                  />
                  <NumberRow
                    label="Max price (credits)"
                    value={form.max_price_credits}
                    onChange={(value) =>
                      setForm((current) => ({ ...current, max_price_credits: value }))
                    }
                    min={1}
                  />
                </div>
                <label className="space-y-2">
                  <span className="wallet-ops-label text-xs">Prompt or input</span>
                  <Textarea
                    value={form.prompt}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, prompt: event.target.value }))
                    }
                    className="min-h-[132px] rounded-none border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] text-sm text-[var(--wallet-ops-fg)] placeholder:text-[var(--wallet-ops-muted)] focus-visible:ring-0 focus-visible:ring-offset-0"
                    placeholder="Describe the task or paste the prompt GENRL should run."
                  />
                </label>
                <div className="grid gap-1 bg-[var(--wallet-ops-border)] md:grid-cols-4">
                  <DataCell label="Route" value={abstractedRoutingLabel} />
                  <DataCell label="Provider" value={abstractedProviderLabel} />
                  <DataCell label="Hardware" value={abstractedHardwareLabel} />
                  <DataCell label="Estimate" value={`~${abstractedRouteEstimate} min`} />
                </div>
                <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-3 py-3 text-xs text-[var(--wallet-ops-muted)]">
                  GENRL will reserve up to{" "}
                  {(abstractedReserveMicros / MICRO).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}{" "}
                  GENRL credits, execute against{" "}
                  {form.provider_slug
                    ? `${abstractedProviderLabel} capacity`
                    : "the best live capacity"}{" "}
                  for {abstractedModelLabel}, charge verified usage, and release anything unused.
                </div>
              </>
            )}

            {selectedAccount && (
              <>
                <SelectRow
                  label="Provider account"
                  value={selectedAccount.id}
                  onChange={(accountID) => {
                    const account = directLaunchAccounts.find((item) => item.id === accountID);
                    if (account) openLaunch(account);
                  }}
                  options={directLaunchAccounts.map((account) => ({
                    value: account.id,
                    label: `${account.display_name} · ${account.provider_name}`,
                  }))}
                />
                {!directModelFreeLaunch && (
                  <label className="space-y-2">
                    <span className="wallet-ops-label text-xs">Prompt or input</span>
                    <Textarea
                      value={form.prompt}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, prompt: event.target.value }))
                      }
                      className="min-h-[112px] rounded-none border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] text-sm text-[var(--wallet-ops-fg)] placeholder:text-[var(--wallet-ops-muted)] focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder="Describe the workload GENRL should run after provisioning."
                    />
                  </label>
                )}

                {selectedCatalogLoading && !selectedCatalog && (
                  <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-3 py-3 text-sm text-[var(--wallet-ops-muted)]">
                    Loading live provider launch options…
                  </div>
                )}

                {selectedCatalogError && (
                  <div className="flex flex-col gap-3 border border-destructive/20 bg-destructive/5 px-3 py-3 text-sm text-destructive md:flex-row md:items-center md:justify-between">
                    <span>{selectedCatalogError}</span>
                    <button
                      type="button"
                      onClick={() => void loadCatalog(selectedAccount, { force: true })}
                      className="inline-flex items-center justify-center border border-destructive/30 px-3 py-1.5 text-xs font-semibold text-destructive transition hover:bg-destructive/10"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {selectedAccount.provider_slug === "hyperstack" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {hyperstackCatalog?.environments.length ? (
                      <SelectRow
                        label="Environment"
                        value={form.environment_name}
                        onChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            environment_name: value,
                            flavor_name: "",
                            image_name: "",
                          }))
                        }
                        options={hyperstackCatalog.environments.map((row) => ({
                          value: row.name,
                          label: row.region ? `${row.name} · ${row.region}` : row.name,
                        }))}
                      />
                    ) : (
                      <InputRow
                        label="Environment name"
                        value={form.environment_name}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, environment_name: value }))
                        }
                        placeholder="default-CANADA-1"
                      />
                    )}
                    {hyperstackFlavorOptions.length ? (
                      <SelectRow
                        label="Flavor"
                        value={form.flavor_name}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, flavor_name: value }))
                        }
                        options={hyperstackFlavorOptions.map((row) => ({
                          value: row.name,
                          disabled: !hyperstackFlavorHasStock(row),
                          label: [
                            row.name,
                            row.gpu ? row.gpu : "",
                            row.gpu_count ? `${row.gpu_count} GPU` : "",
                            hardwareCostBandLabel(
                              hyperstackLaunchableFlavorCostBandByName.get(row.name) || "Mid cost",
                            ),
                            row.stock_available === 0
                              ? "Out of stock"
                              : row.stock_available
                                ? `${row.stock_available} in stock`
                                : "",
                          ]
                            .filter(Boolean)
                            .join(" · "),
                        }))}
                      />
                    ) : (
                      <InputRow
                        label="Flavor name"
                        value={form.flavor_name}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, flavor_name: value }))
                        }
                        placeholder="n3-RTX-A6000x1"
                      />
                    )}
                    {hyperstackImageOptions.length ? (
                      <SelectRow
                        label="Image"
                        value={form.image_name}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, image_name: value }))
                        }
                        options={hyperstackImageOptions.map((row) => ({
                          value: row.name,
                          label: [row.name, row.type || "", row.version || ""]
                            .filter(Boolean)
                            .join(" · "),
                        }))}
                      />
                    ) : (
                      <InputRow
                        label="Image name"
                        value={form.image_name}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, image_name: value }))
                        }
                        placeholder="Ubuntu Server 22.04 LTS R535 CUDA 12.2"
                      />
                    )}
                    {hyperstackKeypairOptions.length ? (
                      <SelectRow
                        label="Keypair"
                        value={form.key_name}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, key_name: value }))
                        }
                        options={hyperstackKeypairOptions.map((row) => ({
                          value: row.name,
                          label: row.name,
                        }))}
                      />
                    ) : (
                      <div className="space-y-2">
                        <span className="wallet-ops-label text-xs">Keypair</span>
                        <div className="border border-[var(--warning)]/30 bg-[var(--warning)]/10 px-3 py-3 text-sm text-[var(--warning)]">
                          Hyperstack requires a keypair for this environment before it can launch.
                        </div>
                      </div>
                    )}
                    <label className="flex items-center gap-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-3 py-3 text-sm md:col-span-2">
                      <input
                        type="checkbox"
                        checked={form.assign_public_ip}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            assign_public_ip: event.target.checked,
                          }))
                        }
                      />
                      Assign a public IP address
                    </label>
                    {launchBlockedReason ? (
                      <div className="border border-[var(--warning)]/30 bg-[var(--warning)]/10 px-3 py-3 text-xs text-[var(--warning)] md:col-span-2">
                        {launchBlockedReason}
                      </div>
                    ) : null}
                  </div>
                ) : selectedAccount.provider_slug === "runpod" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {runPodCatalog?.gpu_types.length ? (
                      <SelectRow
                        label="GPU type"
                        value={form.gpu_type_id}
                        onChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            gpu_type_id: value,
                            cloud_type: "",
                            gpu_count: 1,
                          }))
                        }
                        options={runPodCatalog.gpu_types.map((row) => ({
                          value: row.id,
                          disabled: !runPodGPUTypeLaunchable(row),
                          label: [
                            row.display_name,
                            row.memory_gb ? `${row.memory_gb} GB` : "",
                            hardwareCostBandLabel(
                              runPodLaunchableGPUCostBandByID.get(row.id) || "Mid cost",
                            ),
                          ]
                            .filter(Boolean)
                            .join(" · "),
                        }))}
                      />
                    ) : (
                      <InputRow
                        label="GPU type ID"
                        value={form.gpu_type_id}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, gpu_type_id: value }))
                        }
                        placeholder="NVIDIA RTX A6000 / provider GPU id"
                      />
                    )}
                    {runPodCloudOptions.length ? (
                      <SelectRow
                        label="Cloud type"
                        value={form.cloud_type}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, cloud_type: value, gpu_count: 1 }))
                        }
                        options={runPodCloudOptions.map((value) => ({
                          value,
                          disabled: !runPodCatalogPriceLaunchable(
                            runPodPriceForCloud(selectedRunPodGPU, value),
                          ),
                          label: [
                            runPodCloudLabel(value),
                            formatStockLabel(
                              runPodPriceForCloud(selectedRunPodGPU, value)?.stock_status,
                            ),
                            hardwareCostBandLabel(
                              runPodLaunchableCloudCostBandByType.get(value) || "Mid cost",
                            ),
                          ]
                            .filter(Boolean)
                            .join(" · "),
                        }))}
                      />
                    ) : (
                      <InputRow
                        label="Cloud type"
                        value={form.cloud_type}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, cloud_type: value }))
                        }
                        placeholder="COMMUNITY or SECURE"
                      />
                    )}
                    {runPodGpuCountOptions.length ? (
                      <SelectRow
                        label="GPU count"
                        value={String(form.gpu_count)}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, gpu_count: Number(value || 1) }))
                        }
                        options={runPodGpuCountOptions.map((value) => ({
                          value: String(value),
                          label: `${value} GPU${value === 1 ? "" : "s"}`,
                        }))}
                      />
                    ) : (
                      <NumberRow
                        label="GPU count"
                        value={form.gpu_count}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, gpu_count: value }))
                        }
                        min={1}
                      />
                    )}
                    <InputRow
                      label="Docker image"
                      value={form.docker_image}
                      onChange={(value) =>
                        setForm((current) => ({ ...current, docker_image: value }))
                      }
                      placeholder="vllm/vllm-openai:latest"
                    />
                    <InputRow
                      label="Start command"
                      value={form.start_command}
                      onChange={(value) =>
                        setForm((current) => ({ ...current, start_command: value }))
                      }
                      placeholder="sleep infinity"
                    />
                    <InputRow
                      label="Port spec"
                      value={form.port_spec}
                      onChange={(value) => setForm((current) => ({ ...current, port_spec: value }))}
                      placeholder="8000/http"
                    />
                    <NumberRow
                      label="Container disk (GB)"
                      value={form.container_disk_gb}
                      onChange={(value) =>
                        setForm((current) => ({ ...current, container_disk_gb: value }))
                      }
                      min={1}
                    />
                    <NumberRow
                      label="Volume (GB)"
                      value={form.volume_gb}
                      onChange={(value) => setForm((current) => ({ ...current, volume_gb: value }))}
                      min={1}
                    />
                    {selectedRunPodGPU && (
                      <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-3 py-3 text-xs text-[var(--wallet-ops-muted)] md:col-span-2">
                        {[
                          selectedRunPodGPU.display_name,
                          selectedRunPodGPU.memory_gb
                            ? `${selectedRunPodGPU.memory_gb} GB VRAM`
                            : "",
                          formatStockLabel(
                            runPodPriceForCloud(selectedRunPodGPU, form.cloud_type)?.stock_status,
                          ),
                          hardwareCostBandLabel(
                            runPodLaunchableCloudCostBandByType.get(form.cloud_type) || "Mid cost",
                          ),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                ) : selectedAccountDescriptor && registryLaunchFields.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {registryLaunchFields.map((field) => (
                      <RegistryLaunchInputField
                        key={field.key}
                        field={field}
                        value={providerInputValues[field.key]}
                        options={registryLaunchFieldOptions(
                          selectedAccountDescriptor,
                          field,
                          selectedCatalog,
                          providerInputValues,
                        )}
                        onChange={(value) =>
                          setProviderInputValues((current) =>
                            syncRegistryLaunchInputs(
                              selectedAccountDescriptor,
                              selectedCatalog,
                              {
                                ...current,
                                [field.key]: providerInputValue(field, value),
                              },
                            ),
                          )
                        }
                      />
                    ))}
                    <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-3 py-3 text-xs text-[var(--wallet-ops-muted)] md:col-span-2">
                      Provider eligibility is based on the current catalog. Capacity remains unconfirmed until the provider accepts creation; fields without returned options require an exact provider ID.
                    </div>
                    {launchBlockedReason ? (
                      <div className="border border-[var(--warning)]/30 bg-[var(--warning)]/10 px-3 py-3 text-xs text-[var(--warning)] md:col-span-2">
                        {launchBlockedReason}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="border border-[var(--warning)]/30 bg-[var(--warning)]/10 px-3 py-3 text-sm text-[var(--warning)]">
                    This provider has not published a direct-launch input contract yet.
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--wallet-ops-border)] px-5 py-4">
            <p className="text-xs text-[var(--wallet-ops-muted)]">
              Provider access, bids, and provisioning stay internal to GENRL.
            </p>
            <button
              type="button"
              onClick={() => void handleLaunch()}
              disabled={
                launching ||
                !String(form.name || "").trim() ||
                (!!selectedAccount && !!launchBlockedReason) ||
                (!directModelFreeLaunch && !String(form.prompt || "").trim())
              }
              className="wallet-ops-button disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Rocket className="h-4 w-4" />
              {launching ? "Launching…" : selectedAccount ? "Launch" : "Confirm and run"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="!flex max-h-[calc(100svh-2rem)] max-w-3xl flex-col gap-0 overflow-hidden border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-0 text-[var(--wallet-ops-fg)] sm:rounded-none">
          <DialogHeader className="shrink-0 border-b border-[var(--wallet-ops-border)] px-5 py-4">
            <DialogTitle className="text-base font-semibold tracking-normal">
              {selectedJob ? workloadServiceName(selectedJob) : "Provider job"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[var(--wallet-ops-muted)]">
              {selectedJob
                ? selectedJobManaged
                  ? "GENRL provisions the provider resource, runs the launch prompt automatically, and shows the result here."
                  : `${selectedJob.provider_name} resource from ${selectedJob.account_name}. Click through to inspect the runtime, refresh status, or run inference when the provider endpoint supports it.`
                : "Choose a provider job to inspect."}
            </DialogDescription>
            {selectedJob ? (
              <div className="mt-3 flex items-center gap-1" role="tablist" aria-label="Job access mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={detailTab === "direct"}
                  onClick={() => setDetailTab("direct")}
                  className={cn(
                    "border px-3 py-1.5 text-xs font-semibold transition-colors",
                    detailTab === "direct"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-[var(--wallet-ops-border)] text-[var(--wallet-ops-muted)] hover:text-[var(--wallet-ops-fg)]",
                  )}
                >
                  Direct
                </button>
                {selectedJob.web_terminal_supported ? (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "ssh"}
                    onClick={() => setDetailTab("ssh")}
                    className={cn(
                      "border px-3 py-1.5 text-xs font-semibold transition-colors",
                      detailTab === "ssh"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-[var(--wallet-ops-border)] text-[var(--wallet-ops-muted)] hover:text-[var(--wallet-ops-fg)]",
                    )}
                  >
                    SSH Terminal
                  </button>
                ) : null}
              </div>
            ) : null}
          </DialogHeader>
          {selectedJob ? (
            detailTab === "ssh" && selectedJob.web_terminal_supported ? (
              <div className="min-h-0 flex-1 overflow-hidden p-4" role="tabpanel" aria-label="SSH Terminal">
                <WebSSHTerminal key={selectedJob.id} jobId={selectedJob.id} />
              </div>
            ) : (
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4" role="tabpanel" aria-label="Direct">
              {(() => {
                const runtimeResult = managedRuntimeResult(selectedJob);
                const runtimeState = managedRuntimeStatus(selectedJob);
                const runtimeError = managedRuntimeError(selectedJob);
                const runtimePrompt = managedRuntimePrompt(selectedJob);
                const verificationOnly = runtimeResultIsVerificationOnly(runtimeResult);
                return (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold",
                          statusTone(selectedJob.status),
                        )}
                      >
                        {jobStatusLabel(selectedJob.status)}
                      </span>
                      <span className="inline-flex items-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2.5 py-1 text-[10px] font-semibold text-[var(--wallet-ops-fg)]">
                        {providerStatusLabel(selectedJob)}
                      </span>
                      <span className="inline-flex items-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2.5 py-1 text-[10px] font-semibold text-[var(--wallet-ops-muted)]">
                        {selectedJob.provider_name}
                      </span>
                      <span className="inline-flex items-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2.5 py-1 text-[10px] font-semibold text-[var(--wallet-ops-muted)]">
                        Updated {relTime(selectedJob.updated_at)}
                      </span>
                    </div>

                    <div className="grid gap-1 bg-[var(--wallet-ops-border)] md:grid-cols-2">
                      <DataCell label="Remote ID" value={selectedJob.remote_id} />
                      <DataCell label="Provider wallet" value={selectedJob.account_name} />
                      <DataCell label="Provider status" value={providerStatusLabel(selectedJob)} />
                      <DataCell label="App state" value={jobStatusLabel(selectedJob.status)} />
                      <DataCell label="Compute allocation" value={providerComputeStateLabel(selectedJob)} />
                      <DataCell label="Billable resources" value={providerBillableResourcesLabel(selectedJob)} />
                      <DataCell label="Region / env" value={selectedJob.region || "—"} />
                      <DataCell label="Accelerator / flavor" value={providerAcceleratorLabel(selectedJob)} />
                      <DataCell label="Image" value={selectedJob.image || "—"} />
                      <DataCell label="Created" value={relTime(selectedJob.created_at)} />
                      <DataCell label="Context" value={workloadContextLabel(selectedJob)} />
                    </div>

                    {(runtimeResult || runtimeState) && (
                      <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-4">
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-primary" />
                          <div>
                            <p className="wallet-ops-label">Managed workload</p>
                            <p className="mt-1 text-sm text-[var(--wallet-ops-muted)]">
                              {verificationOnly
                                ? "This legacy run only verified VM/runtime execution; it did not return a real model answer."
                                : runtimeResult
                                  ? `${runtimeResult.model || "Runtime model"} answered the launch prompt.`
                                  : runtimeState === "starting"
                                    ? "GENRL is waiting for the runtime to become ready, then it will run the launch prompt."
                                    : runtimeState === "ready"
                                      ? "Runtime is ready and the launch prompt is being executed."
                                      : runtimeError || "Managed runtime is still in progress."}
                            </p>
                          </div>
                        </div>
                        {runtimeResult && (
                          <div className="mt-4 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="wallet-ops-label text-[10px]">
                                {verificationOnly ? "Verification output" : "Prompt result"}
                              </p>
                              <button
                                type="button"
                                onClick={() => void copyRuntimeOutput(runtimeResult.output_text)}
                                className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                              >
                                <Copy className="h-3.5 w-3.5" />
                                Copy output
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-[var(--wallet-ops-muted)]">
                              {runtimeResultModeLabel(runtimeResult)} ·{" "}
                              {runtimeResult.model || "model"} · {relTime(runtimeResult.checked_at)}
                            </p>
                            {runtimePrompt && (
                              <div className="mt-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-3">
                                <p className="wallet-ops-label text-[10px]">Prompt</p>
                                <p className="mt-2 whitespace-pre-wrap break-words text-xs text-[var(--wallet-ops-muted)]">
                                  {runtimePrompt}
                                </p>
                              </div>
                            )}
                            <pre className="mt-3 whitespace-pre-wrap break-words text-sm text-[var(--wallet-ops-fg)]">
                              {runtimeResult.output_text}
                            </pre>
                            {runSnapshotID(runtimeResult) && (
                              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--wallet-ops-border)] pt-3">
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--wallet-ops-muted)]">
                                  <FileText className="h-3.5 w-3.5" />
                                  Snapshot saved
                                </span>
                                <a
                                  href={runSnapshotFileURL(runtimeResult, "output")}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                                >
                                  Output <ExternalLink className="h-3 w-3" />
                                </a>
                                <a
                                  href={runSnapshotFileURL(runtimeResult, "manifest")}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                                >
                                  Manifest <ExternalLink className="h-3 w-3" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => void handleUseRunSnapshot(runSnapshotID(runtimeResult))}
                                  className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                                >
                                  Use snapshot <RotateCcw className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="wallet-ops-label">Provider actions</p>
                          <p className="mt-1 text-sm text-[var(--wallet-ops-muted)]">
                            {selectedJobManaged
                              ? "GENRL manages execution for this workload. Refresh to follow runtime progress or inspect the advertised runtime endpoint."
                              : jobProbe?.message ||
                                (selectedJobSupportsConsole
                                  ? "Use the provider console to operate this VM directly, then probe the runtime once you start a server on it."
                                  : "We’ll probe the runtime and show only the actions this provider resource actually supports.")}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => void refresh({ silent: true })}
                            className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                          >
                            <RefreshCcw
                              className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
                            />
                            Refresh jobs
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleProbeJob(selectedJob.id)}
                            disabled={probingJob}
                            className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {probingJob ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Activity className="h-3.5 w-3.5" />
                            )}
                            Probe runtime
                          </button>
                          {selectedJobSupportsConsole && (
                            <button
                              type="button"
                              onClick={() => void handleOpenConsole(selectedJob)}
                              disabled={openingConsole}
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {openingConsole ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <ExternalLink className="h-3.5 w-3.5" />
                              )}
                              Open console
                            </button>
                          )}
                          {selectedJobRuntime && (
                            <a
                              href={selectedJobRuntime}
                              target="_blank"
                              rel="noreferrer"
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                            >
                              Open runtime <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {selectedJobDocsURL && (
                            <a
                              href={selectedJobDocsURL}
                              target="_blank"
                              rel="noreferrer"
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                            >
                              Open docs <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {selectedJobCanStart && (
                            <button
                              type="button"
                              onClick={() => void handleProviderAction(selectedJob, "start")}
                              disabled={!!providerActionBusy}
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {providerActionBusy === "start" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Play className="h-3.5 w-3.5" />
                              )}
                              Start
                            </button>
                          )}
                          {selectedJobCanReboot && (
                            <button
                              type="button"
                              onClick={() =>
                                void handleProviderAction(selectedJob, selectedJobRestartAction)
                              }
                              disabled={!!providerActionBusy}
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {providerActionBusy === selectedJobRestartAction ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="h-3.5 w-3.5" />
                              )}
                              Reboot
                            </button>
                          )}
                          {selectedJobCanHibernate && (
                            <button
                              type="button"
                              onClick={() => void handleProviderAction(selectedJob, "hibernate")}
                              disabled={!!providerActionBusy}
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {providerActionBusy === "hibernate" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <MoonStar className="h-3.5 w-3.5" />
                              )}
                              Hibernate
                            </button>
                          )}
                          {selectedJobCanRestore && (
                            <button
                              type="button"
                              onClick={() =>
                                void handleProviderAction(selectedJob, "hibernate-restore")
                              }
                              disabled={!!providerActionBusy}
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {providerActionBusy === "hibernate-restore" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Power className="h-3.5 w-3.5" />
                              )}
                              Restore
                            </button>
                          )}
                          {(selectedJobCanStop ||
                            ["launching", "queued", "unknown"].includes(selectedJob.status)) && (
                            <button
                              type="button"
                              onClick={() => void handleProviderAction(selectedJob, "stop")}
                              disabled={!!providerActionBusy}
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {providerActionBusy === "stop" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Square className="h-3.5 w-3.5" />
                              )}
                              Stop
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 grid gap-1 bg-[var(--wallet-ops-border)] md:grid-cols-2">
                        <div className="bg-[var(--wallet-ops-surface)] p-3">
                          <p className="wallet-ops-label text-[10px]">Runtime mode</p>
                          <p className="mt-1 text-sm font-medium text-[var(--wallet-ops-fg)]">
                            {jobProbe?.mode === "openai-compatible"
                              ? "OpenAI-compatible inference"
                              : jobProbe?.mode === "generic-http"
                                ? jobProbe.reachable
                                  ? "Generic HTTP runtime"
                                  : "Runtime still booting"
                                : selectedJobSupportsConsole
                                  ? "Raw compute with console access"
                                  : "Raw compute only"}
                          </p>
                          <p className="mt-2 text-xs text-[var(--wallet-ops-muted)]">
                            {jobProbe?.message ||
                              (selectedJobSupportsConsole
                                ? "This VM is live compute. Use the provider console to install software, boot an inference server, or open a shell on the machine."
                                : "Launches begin as raw compute unless the provider runtime exposes an HTTP or OpenAI-compatible endpoint.")}
                          </p>
                        </div>
                        <div className="bg-[var(--wallet-ops-surface)] p-3">
                          <p className="wallet-ops-label text-[10px]">Connection details</p>
                          <div className="mt-2 space-y-1.5 text-xs text-[var(--wallet-ops-muted)]">
                            <p>
                              Runtime URL:{" "}
                              <span className="font-medium text-[var(--wallet-ops-fg)]">
                                {selectedJobRuntime || "Not advertised yet"}
                              </span>
                            </p>
                            <p>
                              Public IP:{" "}
                              <span className="font-medium text-[var(--wallet-ops-fg)]">
                                {jobProbe?.public_ip || selectedJob.public_ip || "—"}
                              </span>
                            </p>
                            <p>
                              Keypair:{" "}
                              <span className="font-medium text-[var(--wallet-ops-fg)]">
                                {jobProbe?.keypair_name || selectedJobKeypair || "—"}
                              </span>
                            </p>
                            <p>
                              Console:{" "}
                              <span className="font-medium text-[var(--wallet-ops-fg)]">
                                {selectedJobConsoleURL
                                  ? "Ready"
                                  : selectedJobSupportsConsole
                                    ? "Available on demand"
                                    : "Not available"}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {selectedJobConsoleURL && (
                        <div className="mt-4 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-3">
                          <p className="wallet-ops-label text-[10px]">Console session</p>
                          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                            <p className="text-xs text-[var(--wallet-ops-muted)]">
                              Open the browser console to operate the VM directly, then come back
                              here and probe the runtime once your server is up.
                            </p>
                            <a
                              href={selectedJobConsoleURL}
                              target="_blank"
                              rel="noreferrer"
                              className="wallet-ops-button !min-h-0 py-1.5 text-xs"
                            >
                              Open console <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </div>
                      )}

                      {Array.isArray(jobProbe?.models) && jobProbe.models.length > 0 && (
                        <div className="mt-4">
                          <p className="wallet-ops-label text-[10px]">Detected models</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {jobProbe.models.map((model) => (
                              <button
                                key={model}
                                type="button"
                                onClick={() => setInferenceModel(model)}
                                className={cn(
                                  "inline-flex items-center border px-2.5 py-1 text-[11px] font-medium transition",
                                  inferenceModel === model
                                    ? "border-primary/40 bg-primary/10 text-primary"
                                    : "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] text-[var(--wallet-ops-muted)] hover:text-[var(--wallet-ops-fg)]",
                                )}
                              >
                                {model}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedJobManaged ? (
                      <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-4">
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-primary" />
                          <div>
                            <p className="wallet-ops-label">GENRL execution</p>
                            <p className="mt-1 text-sm text-[var(--wallet-ops-muted)]">
                              Manual runtime prompts are not needed for wallet-managed workloads.
                              GENRL runs the launch prompt automatically and records the result
                              above.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-4">
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-primary" />
                          <div>
                            <p className="wallet-ops-label">Inference</p>
                            <p className="mt-1 text-sm text-[var(--wallet-ops-muted)]">
                              {inferenceReady
                                ? "Send a test prompt through the provider runtime from this site."
                                : selectedJobRuntime
                                  ? "This resource has a runtime URL, but it is not exposing an OpenAI-compatible endpoint yet."
                                  : "This resource is still raw compute. Start an inference server on the machine before using runtime actions."}
                            </p>
                          </div>
                        </div>

                        {inferenceReady ? (
                          <div className="mt-4 space-y-4">
                            <label className="space-y-2">
                              <span className="wallet-ops-label text-xs">Prompt</span>
                              <Textarea
                                value={inferencePrompt}
                                onChange={(event) => setInferencePrompt(event.target.value)}
                                className="min-h-[120px] rounded-none border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)]"
                                placeholder="Enter a runtime instruction."
                              />
                            </label>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <p className="text-xs text-[var(--wallet-ops-muted)]">
                                {inferenceModel
                                  ? `Using model ${inferenceModel}`
                                  : "We’ll use the first detected runtime model."}
                              </p>
                              <button
                                type="button"
                                onClick={() => void handleRunInference()}
                                disabled={inferring}
                                className="wallet-ops-button disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {inferring ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                                {inferring ? "Running inference…" : "Run inference"}
                              </button>
                            </div>
                            {inferenceResult && (
                              <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
                                <p className="wallet-ops-label text-[10px]">Latest result</p>
                                <p className="mt-1 text-xs text-[var(--wallet-ops-muted)]">
                                  {runtimeResultModeLabel(inferenceResult)} ·{" "}
                                  {inferenceResult.model} · {relTime(inferenceResult.checked_at)}
                                </p>
                                <pre className="mt-3 whitespace-pre-wrap break-words text-sm text-[var(--wallet-ops-fg)]">
                                  {inferenceResult.output_text}
                                </pre>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-4 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4 text-sm text-[var(--wallet-ops-muted)]">
                            {jobProbe?.mode === "raw-compute"
                              ? "This VM is just compute right now. Install or boot an inference server on it first, then come back here and probe the runtime again."
                              : "Once the runtime is reachable and exposes /v1/models, this panel will let you run inference directly from the site."}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedJob.last_error && (
                      <div className="border border-destructive/20 bg-destructive/5 px-3 py-3 text-xs text-destructive">
                        {userSafeLaunchError(selectedJob.last_error)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            )
          ) : (
            <div className="px-5 py-6 text-sm text-[var(--wallet-ops-muted)]">
              Choose a provider job to inspect.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function runtimeSessionCanAcceptNextInput(session: RuntimeSession) {
  return ["idle", "runtime_ready"].includes(String(session.state || "").toLowerCase());
}

function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof Wallet;
  label: string;
  value: number;
  hint: string;
  tone: "mint" | "cyan" | "violet" | "amber";
}) {
  const toneClass = {
    mint: "text-[var(--success)]",
    cyan: "text-primary",
    violet: "text-accent",
    amber: "text-[var(--warning)]",
  }[tone];
  return (
    <div className="flex items-center gap-3 bg-[var(--wallet-ops-surface)] px-4 py-4">
      <div
        className={cn(
          "flex h-9 w-9 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)]",
          toneClass,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="wallet-ops-label text-[10px]">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tracking-normal text-[var(--wallet-ops-fg)]">
            {value}
          </span>
          <span className="truncate text-[11px] text-[var(--wallet-ops-muted)]">{hint}</span>
        </div>
      </div>
    </div>
  );
}

function DataCell({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="compute-workloads-data-cell">
      <p>{label}</p>
      <strong>{value}</strong>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Wallet;
  title: string;
  body: string;
}) {
  return (
    <div className="compute-workloads-empty-state">
      <div>
        <Icon className="h-5 w-5" />
      </div>
      <p>{title}</p>
      <small>{body}</small>
    </div>
  );
}

function InputRow({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}) {
  return (
    <label className="space-y-2">
      <span className="wallet-ops-label text-xs">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        required={required}
        aria-required={required || undefined}
        className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none placeholder:text-[var(--wallet-ops-muted)] focus:border-[var(--wallet-ops-border-strong)]"
      />
    </label>
  );
}

function RegistryLaunchInputField({
  field,
  value,
  options,
  onChange,
}: {
  field: ProviderRegistryInputField;
  value?: ProviderInputValue;
  options: ProviderCatalogOption[];
  onChange: (value: unknown) => void;
}) {
  const label = `${field.label || providerTokenLabel(field.key)}${field.required ? " *" : ""}`;
  if (options.length > 0) {
    return (
      <SelectRow
        label={label}
        value={providerInputDisplayValue(value)}
        onChange={onChange}
        allowBlank={!field.required}
        placeholder={field.required ? "Choose an exact value" : "Use account default"}
        options={options.map((option) => ({
          ...option,
          label: option.detail ? `${option.label} · ${option.detail}` : option.label,
        }))}
      />
    );
  }
  if (field.type === "boolean") {
    return (
      <SelectRow
        label={label}
        value={String(Boolean(value))}
        onChange={onChange}
        options={[
          { value: "false", label: "No" },
          { value: "true", label: "Yes" },
        ]}
      />
    );
  }
  if (field.type === "integer") {
    return (
      <NumberRow
        label={label}
        value={Number(value || 0)}
        onChange={onChange}
        min={field.required ? 1 : 0}
      />
    );
  }
  return (
    <label className="space-y-2">
      <span className="wallet-ops-label text-xs">{label}</span>
      {field.type === "text" ? (
        <textarea
          rows={3}
          value={providerInputDisplayValue(value)}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Enter the exact provider value"
          className="w-full resize-y border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 py-2 font-mono text-xs text-[var(--wallet-ops-fg)] outline-none placeholder:text-[var(--wallet-ops-muted)] focus:border-[var(--wallet-ops-border-strong)]"
        />
      ) : (
        <input
          type={field.secret ? "password" : "text"}
          value={providerInputDisplayValue(value)}
          onChange={(event) => onChange(event.target.value)}
          placeholder={
            providerInputIsList(field)
              ? "Exact values, separated by commas"
              : "Enter the exact provider value"
          }
          className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none placeholder:text-[var(--wallet-ops-muted)] focus:border-[var(--wallet-ops-border-strong)]"
        />
      )}
      {field.options_source ? (
        <span className="block text-[10px] text-[var(--wallet-ops-muted)]">
          No options returned for {field.options_source}; enter the exact ID manually.
        </span>
      ) : null}
    </label>
  );
}

function NumberRow({
  label,
  value,
  onChange,
  min,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
}) {
  return (
    <label className="space-y-2">
      <span className="wallet-ops-label text-xs">{label}</span>
      <input
        type="number"
        min={min}
        value={value}
        onChange={(event) => onChange(Number(event.target.value || 0))}
        className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none placeholder:text-[var(--wallet-ops-muted)] focus:border-[var(--wallet-ops-border-strong)]"
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  onChange,
  options,
  placeholder,
  allowBlank,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  allowBlank?: boolean;
}) {
  return (
    <label className="space-y-2">
      <span className="wallet-ops-label text-xs">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
      >
        {allowBlank ? <option value="">{placeholder || "None"}</option> : null}
        {!allowBlank && placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
